/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { IsotopeProfile, MaterialPreset } from "../types";


export const ISOTOPE_PRESETS: IsotopeProfile[] = [
  {
    id: "sr90",
    name: "Strontium-90 (Sr-90) [Fission Beta Source]",
    type: "beta-",
    baseEnergyMeV: 0.546,
    fluxIntensity: 370000,
    pdgId: 11, // electron
    polarization: 0.15,
    halfLife: "28.8 years",
  },
  {
    id: "cs137",
    name: "Cesium-137 (Cs-137) [Relativistic Recoil Electron]",
    type: "beta-",
    baseEnergyMeV: 0.512,
    fluxIntensity: 550000,
    pdgId: 11,
    polarization: 0.22,
    halfLife: "30.1 years",
  },
  {
    id: "co60",
    name: "Cobalt-60 / Compton Recoil [Compton Scattering e-]",
    type: "beta-",
    baseEnergyMeV: 0.963,
    fluxIntensity: 850000,
    pdgId: 11,
    polarization: 0.45,
    halfLife: "5.27 years",
  },
  {
    id: "p32",
    name: "Phosphorus-32 (P-32) [High-Energy Beta Emitter]",
    type: "beta-",
    baseEnergyMeV: 1.71,
    fluxIntensity: 420000,
    pdgId: 11,
    polarization: 0.18,
    halfLife: "14.3 days",
  },
  {
    id: "y90",
    name: "Yttrium-90 (Y-90) [Pure Beta Emitter]",
    type: "beta-",
    baseEnergyMeV: 2.28,
    fluxIntensity: 380000,
    pdgId: 11,
    polarization: 0.12,
    halfLife: "64 hours",
  },
  {
    id: "muon_cosmic",
    name: "Cosmic Muon (μ-) [Relativistic Atmospheric Track]",
    type: "custom", // mass of muon will be calculated if selected
    baseEnergyMeV: 4000.0, // 4 GeV cosmic rays
    fluxIntensity: 120, // 120/m^2/s/sr
    pdgId: 13,
    polarization: 0.3,
    halfLife: "2.2 microseconds",
  },
  {
    id: "proton_sps",
    name: "Proton (p+) [High-Energy SPS RF Cavity Beam]",
    type: "proton",
    baseEnergyMeV: 450.0, // 450 MeV SPS beam-line
    fluxIntensity: 1200000,
    pdgId: 2212,
    polarization: 0.85,
    halfLife: "Stable",
  },
  {
    id: "proton_therapy",
    name: "Proton (p+) [Clinical Therapy Beam - 70-250 MeV]",
    type: "proton",
    baseEnergyMeV: 150.0,
    fluxIntensity: 2500000,
    pdgId: 2212,
    polarization: 0.0,
    halfLife: "Stable",
  },
  {
    id: "carbon_ion",
    name: "Carbon-12 Ion (C-12) [Hadron Therapy]",
    type: "heavy-ion",
    baseEnergyMeV: 290.0, // 290 MeV/u typical for therapy
    fluxIntensity: 95000,
    pdgId: 1000060120,
    polarization: 0.0,
    halfLife: "Stable",
  },
  {
    id: "heavy_ion",
    name: "Alpha (He-4) [Low-Energy Ion Accelerator]",
    type: "heavy-ion",
    baseEnergyMeV: 120.0,
    fluxIntensity: 180000,
    pdgId: 1000020040,
    polarization: 0.0,
    halfLife: "Stable",
  },
  {
    id: "electron_linac",
    name: "Electron (e-) [Medical LINAC - 6-20 MeV]",
    type: "e-",
    baseEnergyMeV: 12.0,
    fluxIntensity: 5000000,
    pdgId: 11,
    polarization: 0.0,
    halfLife: "Stable",
  },
  {
    id: "am241",
    name: "Americium-241 (Am-241) [Alpha Source]",
    type: "alpha",
    baseEnergyMeV: 5.486,
    fluxIntensity: 85000,
    pdgId: 1000020040,
    polarization: 0.0,
    halfLife: "432 years",
  },
  {
    id: "pu238",
    name: "Plutonium-238 (Pu-238) [RTG Power Source]",
    type: "alpha",
    baseEnergyMeV: 5.593,
    fluxIntensity: 72000,
    pdgId: 1000020040,
    polarization: 0.0,
    halfLife: "87.7 years",
  },
  {
    id: "fe55",
    name: "Iron-55 (Fe-55) [Low-Energy X-Ray Source]",
    type: "ec",
    baseEnergyMeV: 0.006,
    fluxIntensity: 950000,
    pdgId: 11,
    polarization: 0.0,
    halfLife: "2.7 years",
  },
  {
    id: "neutrino_beam",
    name: "Neutrino Beam (νμ) [Fermilab DUNE / MINOS]",
    type: "custom",
    baseEnergyMeV: 2500.0,
    fluxIntensity: 50000000,
    pdgId: 14,
    polarization: 1.0,
    halfLife: "Stable",
  },
  {
    id: "dark_matter_wimp",
    name: "WIMP Candidate (χ) [Dark Matter Recoil Signal]",
    type: "custom",
    baseEnergyMeV: 50000.0,
    fluxIntensity: 5,
    pdgId: 999999,
    polarization: 0.0,
    halfLife: "Stable",
  },
  {
    id: "na22",
    name: "Sodium-22 (Na-22) [Positron Emitter]",
    type: "beta+",
    baseEnergyMeV: 0.545,
    fluxIntensity: 680000,
    pdgId: 11,
    polarization: 0.0,
    halfLife: "2.6 years",
  },
  {
    id: "f18",
    name: "Fluorine-18 (F-18) [PET Imaging]",
    type: "beta+",
    baseEnergyMeV: 0.634,
    fluxIntensity: 4200000,
    pdgId: 11,
    polarization: 0.0,
    halfLife: "110 minutes",
  },
  {
    id: "i131",
    name: "Iodine-131 (I-131) [Therapeutic Isotope]",
    type: "beta-",
    baseEnergyMeV: 0.606,
    fluxIntensity: 750000,
    pdgId: 11,
    polarization: 0.0,
    halfLife: "8 days",
  },
  {
    id: "ra226",
    name: "Radium-226 (Ra-226) [Historical Source]",
    type: "alpha",
    baseEnergyMeV: 4.78,
    fluxIntensity: 45000,
    pdgId: 1000020040,
    polarization: 0.0,
    halfLife: "1600 years",
  },
  {
    id: "tritium",
    name: "Tritium (H-3) [Low-Energy Beta]",
    type: "beta-",
    baseEnergyMeV: 0.0186,
    fluxIntensity: 1200000,
    pdgId: 11,
    polarization: 0.0,
    halfLife: "12.3 years",
  },
  {
    id: "neutron_thermal",
    name: "Thermal Neutron (n) [Reactor Core]",
    type: "neutron",
    baseEnergyMeV: 0.025,
    fluxIntensity: 1500000,
    pdgId: 2112,
    polarization: 0.0,
    halfLife: "Stable",
  },
  {
    id: "neutron_fast",
    name: "Fast Neutron (n) [Spallation Source]",
    type: "neutron",
    baseEnergyMeV: 14.1,
    fluxIntensity: 850000,
    pdgId: 2112,
    polarization: 0.0,
    halfLife: "Stable",
  },
  {
    id: "pion_plus",
    name: "Pion+ (π+) [Secondary Beam]",
    type: "meson",
    baseEnergyMeV: 139.57,
    fluxIntensity: 450000,
    pdgId: 211,
    polarization: 0.0,
    halfLife: "26 nanoseconds",
  },
  {
    id: "kaon_plus",
    name: "Kaon+ (K+) [Strange Meson]",
    type: "meson",
    baseEnergyMeV: 493.7,
    fluxIntensity: 320000,
    pdgId: 321,
    polarization: 0.0,
    halfLife: "12.4 nanoseconds",
  },
  {
    id: "tau_lepton",
    name: "Tau Lepton (τ-) [Heavy Lepton]",
    type: "e-",
    baseEnergyMeV: 1776.86,
    fluxIntensity: 85000,
    pdgId: 15,
    polarization: 0.0,
    halfLife: "2.9e-13 seconds",
  },
  {
    id: "w_boson",
    name: "W Boson [Weak Force Carrier]",
    type: "custom",
    baseEnergyMeV: 80379.0,
    fluxIntensity: 10,
    pdgId: 24,
    polarization: 0.0,
    halfLife: "3e-25 seconds",
  },
  {
    id: "z_boson",
    name: "Z Boson [Neutral Weak Carrier]",
    type: "custom",
    baseEnergyMeV: 91187.6,
    fluxIntensity: 8,
    pdgId: 23,
    polarization: 0.0,
    halfLife: "3e-25 seconds",
  },
  {
    id: "higgs_boson",
    name: "Higgs Boson [Mass Generator]",
    type: "custom",
    baseEnergyMeV: 125000.0,
    fluxIntensity: 1,
    pdgId: 25,
    polarization: 0.0,
    halfLife: "1.6e-22 seconds",
  },
  {
    id: "antiproton",
    name: "Antiproton (p-) [Antimatter]",
    type: "proton",
    baseEnergyMeV: 938.27,
    fluxIntensity: 150000,
    pdgId: -2212,
    polarization: 0.0,
    halfLife: "Stable",
  },
  {
    id: "positron",
    name: "Positron (e+) [Antimatter Electron]",
    type: "beta+",
    baseEnergyMeV: 0.511,
    fluxIntensity: 280000,
    pdgId: -11,
    polarization: 0.0,
    halfLife: "Stable",
  },
  {
    id: "deuteron",
    name: "Deuteron (²H) [Heavy Hydrogen]",
    type: "custom",
    baseEnergyMeV: 1875.61,
    fluxIntensity: 420000,
    pdgId: 1000010020,
    polarization: 0.0,
    halfLife: "Stable",
  },
  {
    id: "triton",
    name: "Triton (³H) [Tritium Nucleus]",
    type: "custom",
    baseEnergyMeV: 2808.92,
    fluxIntensity: 180000,
    pdgId: 1000010030,
    polarization: 0.0,
    halfLife: "12.3 years",
  },
  {
    id: "helium_3",
    name: "Helium-3 (³He) [Fusion Fuel]",
    type: "custom",
    baseEnergyMeV: 2808.39,
    fluxIntensity: 350000,
    pdgId: 1000020030,
    polarization: 0.0,
    halfLife: "Stable",
  },
  {
    id: "boron_10",
    name: "Boron-10 (¹⁰B) [Neutron Capture]",
    type: "custom",
    baseEnergyMeV: 9324.44,
    fluxIntensity: 95000,
    pdgId: 1000050100,
    polarization: 0.0,
    halfLife: "Stable",
  },
  {
    id: "gadolinium_157",
    name: "Gadolinium-157 (¹⁵⁷Gd) [Neutron Detector]",
    type: "custom",
    baseEnergyMeV: 146185.0,
    fluxIntensity: 45000,
    pdgId: 1000641570,
    polarization: 0.0,
    halfLife: "Stable",
  },
  {
    id: "ir192",
    name: "Iridium-192 (Ir-192) [Industrial Gamma Source]",
    type: "custom",
    baseEnergyMeV: 0.308,
    fluxIntensity: 850000,
    pdgId: 1000771920,
    polarization: 0.0,
    halfLife: "73.8 days",
  },
  {
    id: "se75",
    name: "Selenium-75 (Se-75) [Industrial Radiography]",
    type: "custom",
    baseEnergyMeV: 0.265,
    fluxIntensity: 650000,
    pdgId: 1000340750,
    polarization: 0.0,
    halfLife: "120 days",
  },
  {
    id: "yb169",
    name: "Ytterbium-169 (Yb-169) [Portable XRF Source]",
    type: "custom",
    baseEnergyMeV: 0.063,
    fluxIntensity: 1200000,
    pdgId: 1000701690,
    polarization: 0.0,
    halfLife: "32 days",
  },
  {
    id: "cd109",
    name: "Cadmium-109 (Cd-109) [Calibration Source]",
    type: "ec",
    baseEnergyMeV: 0.022,
    fluxIntensity: 2500000,
    pdgId: 1000481090,
    polarization: 0.0,
    halfLife: "462 days",
  },
  {
    id: "co57",
    name: "Cobalt-57 (Co-57) [Mössbauer Source]",
    type: "ec",
    baseEnergyMeV: 0.122,
    fluxIntensity: 1800000,
    pdgId: 1000270570,
    polarization: 0.0,
    halfLife: "271 days",
  },
  {
    id: "sn113",
    name: "Tin-113 (Sn-113) [Medical Calibration]",
    type: "ec",
    baseEnergyMeV: 0.391,
    fluxIntensity: 950000,
    pdgId: 1000501130,
    polarization: 0.0,
    halfLife: "115 days",
  },
  {
    id: "te99m",
    name: "Technetium-99m (Tc-99m) [SPECT Imaging]",
    type: "custom",
    baseEnergyMeV: 0.140,
    fluxIntensity: 3500000,
    pdgId: 1000430990,
    polarization: 0.0,
    halfLife: "6 hours",
  },
  {
    id: "i125",
    name: "Iodine-125 (I-125) [Brachytherapy]",
    type: "ec",
    baseEnergyMeV: 0.035,
    fluxIntensity: 4200000,
    pdgId: 1000531250,
    polarization: 0.0,
    halfLife: "60 days",
  },
  {
    id: "pd103",
    name: "Palladium-103 (Pd-103) [Brachytherapy]",
    type: "ec",
    baseEnergyMeV: 0.021,
    fluxIntensity: 3800000,
    pdgId: 1000461030,
    polarization: 0.0,
    halfLife: "17 days",
  },
  {
    id: "cs131",
    name: "Cesium-131 (Cs-131) [Brachytherapy]",
    type: "ec",
    baseEnergyMeV: 0.030,
    fluxIntensity: 3100000,
    pdgId: 1000551310,
    polarization: 0.0,
    halfLife: "9.7 days",
  },
  {
    id: "kr85",
    name: "Krypton-85 (Kr-85) [Gas Source]",
    type: "beta-",
    baseEnergyMeV: 0.687,
    fluxIntensity: 1500000,
    pdgId: 1000360850,
    polarization: 0.0,
    halfLife: "10.8 years",
  },
  {
    id: "h3",
    name: "Helium-3 (³He) [Neutron Detector Gas]",
    type: "custom",
    baseEnergyMeV: 0.000,
    fluxIntensity: 500000,
    pdgId: 1000020030,
    polarization: 0.0,
    halfLife: "Stable",
  },
  {
    id: "li6",
    name: "Lithium-6 (⁶Li) [Neutron Absorber]",
    type: "custom",
    baseEnergyMeV: 0.000,
    fluxIntensity: 800000,
    pdgId: 1000030060,
    polarization: 0.0,
    halfLife: "Stable",
  },
  {
    id: "b10",
    name: "Boron-10 (¹⁰B) [Neutron Capture Therapy]",
    type: "custom",
    baseEnergyMeV: 0.000,
    fluxIntensity: 1200000,
    pdgId: 1000050100,
    polarization: 0.0,
    halfLife: "Stable",
  },
  {
    id: "gd153",
    name: "Gadolinium-153 (¹⁵³Gd) [Calibration Source]",
    type: "ec",
    baseEnergyMeV: 0.083,
    fluxIntensity: 2200000,
    pdgId: 1000641530,
    polarization: 0.0,
    halfLife: "240 days",
  },
  {
    id: "eu152",
    name: "Europium-152 (¹⁵²Eu) [Multi-line Source]",
    type: "ec",
    baseEnergyMeV: 0.122,
    fluxIntensity: 1700000,
    pdgId: 1000631520,
    polarization: 0.0,
    halfLife: "13.5 years",
  },
  {
    id: "ba133",
    name: "Barium-133 (¹³³Ba) [Calibration Standard]",
    type: "ec",
    baseEnergyMeV: 0.356,
    fluxIntensity: 1400000,
    pdgId: 1000561330,
    polarization: 0.0,
    halfLife: "10.5 years",
  },
  {
    id: "co56",
    name: "Cobalt-56 (Co-56) [Activation Analysis]",
    type: "beta+",
    baseEnergyMeV: 0.459,
    fluxIntensity: 750000,
    pdgId: 1000270560,
    polarization: 0.0,
    halfLife: "77 days",
  },
  {
    id: "zn65",
    name: "Zinc-65 (Zn-65) [Tracer Isotope]",
    type: "beta+",
    baseEnergyMeV: 0.325,
    fluxIntensity: 900000,
    pdgId: 1000300650,
    polarization: 0.0,
    halfLife: "244 days",
  },
  {
    id: "na24",
    name: "Sodium-24 (Na-24) [Reactor Tracer]",
    type: "beta-",
    baseEnergyMeV: 1.389,
    fluxIntensity: 1100000,
    pdgId: 1000110240,
    polarization: 0.0,
    halfLife: "15 hours",
  },
  {
    id: "k40",
    name: "Potassium-40 (K-40) [Natural Radioisotope]",
    type: "beta-",
    baseEnergyMeV: 1.311,
    fluxIntensity: 300000,
    pdgId: 1000190400,
    polarization: 0.0,
    halfLife: "1.25 billion years",
  },
  {
    id: "u238",
    name: "Uranium-238 (U-238) [Fertile Isotope]",
    type: "alpha",
    baseEnergyMeV: 4.27,
    fluxIntensity: 25000,
    pdgId: 1000922380,
    polarization: 0.0,
    halfLife: "4.5 billion years",
  },
  {
    id: "th232",
    name: "Thorium-232 (Th-232) [Fertile Isotope]",
    type: "alpha",
    baseEnergyMeV: 4.08,
    fluxIntensity: 22000,
    pdgId: 1000902320,
    polarization: 0.0,
    halfLife: "14 billion years",
  },
  {
    id: "pu239",
    name: "Plutonium-239 (Pu-239) [Fissile Material]",
    type: "alpha",
    baseEnergyMeV: 5.157,
    fluxIntensity: 18000,
    pdgId: 1000942390,
    polarization: 0.0,
    halfLife: "24,100 years",
  },
  {
    id: "cm244",
    name: "Curium-244 (Cm-244) [RTG Alternative]",
    type: "alpha",
    baseEnergyMeV: 5.805,
    fluxIntensity: 15000,
    pdgId: 1000962440,
    polarization: 0.0,
    halfLife: "18.1 years",
  },
  {
    id: "sr89",
    name: "Strontium-89 (Sr-89) [Medical Therapy]",
    type: "beta-",
    baseEnergyMeV: 0.583,
    fluxIntensity: 680000,
    pdgId: 1000380890,
    polarization: 0.0,
    halfLife: "50.5 days",
  },
  {
    id: "sm153",
    name: "Samarium-153 (Sm-153) [Bone Pain Therapy]",
    type: "beta-",
    baseEnergyMeV: 0.808,
    fluxIntensity: 520000,
    pdgId: 1000621530,
    polarization: 0.0,
    halfLife: "46.3 hours",
  },
  {
    id: "lu177",
    name: "Lutetium-177 (Lu-177) [Theranostics]",
    type: "beta-",
    baseEnergyMeV: 0.497,
    fluxIntensity: 2800000,
    pdgId: 1000711770,
    polarization: 0.0,
    halfLife: "6.7 days",
  },
  {
    id: "ac225",
    name: "Actinium-225 (Ac-225) [Alpha Therapy]",
    type: "alpha",
    baseEnergyMeV: 5.83,
    fluxIntensity: 85000,
    pdgId: 1000892250,
    polarization: 0.0,
    halfLife: "10 days",
  },
  {
    id: "at211",
    name: "Astatine-211 (At-211) [Alpha Therapy]",
    type: "alpha",
    baseEnergyMeV: 5.87,
    fluxIntensity: 72000,
    pdgId: 1000852110,
    polarization: 0.0,
    halfLife: "7.2 hours",
  },
  {
    id: "bi212",
    name: "Bismuth-212 (Bi-212) [Generator Parent]",
    type: "beta-",
    baseEnergyMeV: 2.254,
    fluxIntensity: 450000,
    pdgId: 1000832120,
    polarization: 0.0,
    halfLife: "60.6 minutes",
  },
  {
    id: "pb212",
    name: "Lead-212 (Pb-212) [Generator Daughter]",
    type: "beta-",
    baseEnergyMeV: 0.574,
    fluxIntensity: 380000,
    pdgId: 1000822120,
    polarization: 0.0,
    halfLife: "10.6 hours",
  },
  {
    id: "po210",
    name: "Polonium-210 (Po-210) [Alpha Source]",
    type: "alpha",
    baseEnergyMeV: 5.304,
    fluxIntensity: 95000,
    pdgId: 1000842100,
    polarization: 0.0,
    halfLife: "138 days",
  },
  {
    id: "rn222",
    name: "Radon-222 (Rn-222) [Gas Daughter]",
    type: "alpha",
    baseEnergyMeV: 5.590,
    fluxIntensity: 65000,
    pdgId: 1000862220,
    polarization: 0.0,
    halfLife: "3.8 days",
  },
  {
    id: "po218",
    name: "Polonium-218 (Po-218) [Radon Daughter]",
    type: "alpha",
    baseEnergyMeV: 6.115,
    fluxIntensity: 58000,
    pdgId: 1000842180,
    polarization: 0.0,
    halfLife: "3.1 minutes",
  },
  {
    id: "deuterium",
    name: "Deuterium (²H) [Fusion Fuel]",
    type: "custom",
    baseEnergyMeV: 1875.61,
    fluxIntensity: 1500000,
    pdgId: 1000010020,
    polarization: 0.0,
    halfLife: "Stable",
  },
  {
    id: "tritium_gas",
    name: "Tritium Gas (³H) [Self-luminous]",
    type: "beta-",
    baseEnergyMeV: 0.0186,
    fluxIntensity: 2500000,
    pdgId: 1000010030,
    polarization: 0.0,
    halfLife: "12.3 years",
  },
  {
    id: "c14",
    name: "Carbon-14 (C-14) [Radiocarbon Dating]",
    type: "beta-",
    baseEnergyMeV: 0.156,
    fluxIntensity: 800000,
    pdgId: 1000060140,
    polarization: 0.0,
    halfLife: "5730 years",
  },
  {
    id: "ca41",
    name: "Calcium-41 (Ca-41) [Long-lived Beta]",
    type: "beta-",
    baseEnergyMeV: 0.421,
    fluxIntensity: 450000,
    pdgId: 1000200410,
    polarization: 0.0,
    halfLife: "99,400 years",
  },
  {
    id: "cl36",
    name: "Chlorine-36 (Cl-36) [Environmental Tracer]",
    type: "beta-",
    baseEnergyMeV: 0.714,
    fluxIntensity: 350000,
    pdgId: 1000170360,
    polarization: 0.0,
    halfLife: "301,000 years",
  },
  {
    id: "ni63",
    name: "Nickel-63 (Ni-63) [Betavoltaic Source]",
    type: "beta-",
    baseEnergyMeV: 0.067,
    fluxIntensity: 3200000,
    pdgId: 1000280630,
    polarization: 0.0,
    halfLife: "100 years",
  },
  {
    id: "pm147",
    name: "Promethium-147 (Pm-147) [Beta Source]",
    type: "beta-",
    baseEnergyMeV: 0.225,
    fluxIntensity: 1800000,
    pdgId: 1000611470,
    polarization: 0.0,
    halfLife: "2.6 years",
  },
  {
    id: "tc99",
    name: "Technetium-99 (Tc-99) [Fission Product]",
    type: "beta-",
    baseEnergyMeV: 0.293,
    fluxIntensity: 1200000,
    pdgId: 1000430990,
    polarization: 0.0,
    halfLife: "211,000 years",
  },
  {
    id: "i129",
    name: "Iodine-129 (I-129) [Long-lived Fission Product]",
    type: "beta-",
    baseEnergyMeV: 0.154,
    fluxIntensity: 650000,
    pdgId: 1000531290,
    polarization: 0.0,
    halfLife: "15.7 million years",
  },
  {
    id: "cs137_detailed",
    name: "Cesium-137 (Cs-137) [Detailed Model]",
    type: "beta-",
    baseEnergyMeV: 0.514,
    fluxIntensity: 580000,
    pdgId: 1000551370,
    polarization: 0.0,
    halfLife: "30.1 years",
  },
  {
    id: "sr90_detailed",
    name: "Strontium-90 (Sr-90) [Detailed Model]",
    type: "beta-",
    baseEnergyMeV: 0.546,
    fluxIntensity: 420000,
    pdgId: 1000380900,
    polarization: 0.0,
    halfLife: "28.8 years",
  },
  {
    id: "y90_detailed",
    name: "Yttrium-90 (Y-90) [Detailed Model]",
    type: "beta-",
    baseEnergyMeV: 0.933,
    fluxIntensity: 380000,
    pdgId: 1000390900,
    polarization: 0.0,
    halfLife: "64 hours",
  },
  {
    id: "p32_detailed",
    name: "Phosphorus-32 (P-32) [Detailed Model]",
    type: "beta-",
    baseEnergyMeV: 1.709,
    fluxIntensity: 450000,
    pdgId: 1000150320,
    polarization: 0.0,
    halfLife: "14.3 days",
  },
  {
    id: "s35",
    name: "Sulfur-35 (S-35) [Low-energy Beta]",
    type: "beta-",
    baseEnergyMeV: 0.167,
    fluxIntensity: 2200000,
    pdgId: 1000160350,
    polarization: 0.0,
    halfLife: "87.5 days",
  },
  {
    id: "ca45",
    name: "Calcium-45 (Ca-45) [Bone Seeker]",
    type: "beta-",
    baseEnergyMeV: 0.257,
    fluxIntensity: 1600000,
    pdgId: 1000200450,
    polarization: 0.0,
    halfLife: "163 days",
  },
  {
    id: "cr51",
    name: "Chromium-51 (Cr-51) [Medical Tracer]",
    type: "ec",
    baseEnergyMeV: 0.320,
    fluxIntensity: 1900000,
    pdgId: 1000240510,
    polarization: 0.0,
    halfLife: "27.7 days",
  },
  {
    id: "mn54",
    name: "Manganese-54 (Mn-54) [Calibration Source]",
    type: "beta+",
    baseEnergyMeV: 0.835,
    fluxIntensity: 1100000,
    pdgId: 1000250540,
    polarization: 0.0,
    halfLife: "312 days",
  },
  {
    id: "fe59",
    name: "Iron-59 (Fe-59) [Metabolism Tracer]",
    type: "beta-",
    baseEnergyMeV: 0.466,
    fluxIntensity: 1300000,
    pdgId: 1000260590,
    polarization: 0.0,
    halfLife: "44.5 days",
  },
  {
    id: "co58",
    name: "Cobalt-58 (Co-58) [Activation Product]",
    type: "beta+",
    baseEnergyMeV: 0.475,
    fluxIntensity: 980000,
    pdgId: 1000270580,
    polarization: 0.0,
    halfLife: "70.8 days",
  },
  {
    id: "cu64",
    name: "Copper-64 (Cu-64) [Theranostic]",
    type: "beta+",
    baseEnergyMeV: 0.579,
    fluxIntensity: 2400000,
    pdgId: 1000290640,
    polarization: 0.0,
    halfLife: "12.7 hours",
  },
  {
    id: "zn63",
    name: "Zinc-63 (Zn-63) [Short-lived Tracer]",
    type: "beta+",
    baseEnergyMeV: 0.325,
    fluxIntensity: 870000,
    pdgId: 1000300630,
    polarization: 0.0,
    halfLife: "38.5 minutes",
  },
  {
    id: "ga67",
    name: "Gallium-67 (Ga-67) [Infection Imaging]",
    type: "ec",
    baseEnergyMeV: 0.093,
    fluxIntensity: 2100000,
    pdgId: 1000310670,
    polarization: 0.0,
    halfLife: "78 hours",
  },
  {
    id: "sr85",
    name: "Strontium-85 (Sr-85) [Bone Imaging]",
    type: "ec",
    baseEnergyMeV: 0.514,
    fluxIntensity: 1400000,
    pdgId: 1000380850,
    polarization: 0.0,
    halfLife: "64.8 days",
  },
  {
    id: "tc99m_detailed",
    name: "Technetium-99m (Tc-99m) [SPECT Standard]",
    type: "custom",
    baseEnergyMeV: 0.140,
    fluxIntensity: 3800000,
    pdgId: 1000430990,
    polarization: 0.0,
    halfLife: "6 hours",
  },
  {
    id: "in111",
    name: "Indium-111 (In-111) [White Blood Cell Imaging]",
    type: "ec",
    baseEnergyMeV: 0.145,
    fluxIntensity: 2600000,
    pdgId: 1000491110,
    polarization: 0.0,
    halfLife: "2.8 days",
  },
  {
    id: "i123",
    name: "Iodine-123 (I-123) [Thyroid Imaging]",
    type: "ec",
    baseEnergyMeV: 0.159,
    fluxIntensity: 2900000,
    pdgId: 1000531230,
    polarization: 0.0,
    halfLife: "13 hours",
  },
  {
    id: "xe133",
    name: "Xenon-133 (Xe-133) [Lung Ventilation]",
    type: "beta-",
    baseEnergyMeV: 0.346,
    fluxIntensity: 1700000,
    pdgId: 1000541330,
    polarization: 0.0,
    halfLife: "5.2 days",
  },
  {
    id: "tl201",
    name: "Thallium-201 (Tl-201) [Cardiac Imaging]",
    type: "ec",
    baseEnergyMeV: 0.135,
    fluxIntensity: 3100000,
    pdgId: 1000812010,
    polarization: 0.0,
    halfLife: "73 hours",
  },
  {
    id: "am241_detailed",
    name: "Americium-241 (Am-241) [Smoke Detector]",
    type: "alpha",
    baseEnergyMeV: 5.486,
    fluxIntensity: 95000,
    pdgId: 1000952410,
    polarization: 0.0,
    halfLife: "432 years",
  },
  {
    id: "cm242",
    name: "Curium-242 (Cm-242) [Industrial Source]",
    type: "alpha",
    baseEnergyMeV: 6.213,
    fluxIntensity: 42000,
    pdgId: 1000962420,
    polarization: 0.0,
    halfLife: "163 days",
  },
  {
    id: "cf252",
    name: "Californium-252 (Cf-252) [Spontaneous Fission]",
    type: "custom",
    baseEnergyMeV: 2.3,
    fluxIntensity: 230000000,
    pdgId: 1000982520,
    polarization: 0.0,
    halfLife: "2.6 years",
  },
  {
    id: "cm244_detailed",
    name: "Curium-244 (Cm-244) [RTG Source]",
    type: "alpha",
    baseEnergyMeV: 5.805,
    fluxIntensity: 15000,
    pdgId: 1000962440,
    polarization: 0.0,
    halfLife: "18.1 years",
  },
  {
    id: "pu238_detailed",
    name: "Plutonium-238 (Pu-238) [RTG Standard]",
    type: "alpha",
    baseEnergyMeV: 5.593,
    fluxIntensity: 72000,
    pdgId: 1000942380,
    polarization: 0.0,
    halfLife: "87.7 years",
  },
  {
    id: "pu240",
    name: "Plutonium-240 (Pu-240) [Weapons Grade]",
    type: "alpha",
    baseEnergyMeV: 5.255,
    fluxIntensity: 35000,
    pdgId: 1000942400,
    polarization: 0.0,
    halfLife: "6,560 years",
  },
  {
    id: "pu241",
    name: "Plutonium-241 (Pu-241) [Beta Emitter]",
    type: "beta-",
    baseEnergyMeV: 0.020,
    fluxIntensity: 850000,
    pdgId: 1000942410,
    polarization: 0.0,
    halfLife: "14.3 years",
  },
  {
    id: "am243",
    name: "Americium-243 (Am-243) [Long-lived Alpha]",
    type: "alpha",
    baseEnergyMeV: 5.275,
    fluxIntensity: 28000,
    pdgId: 1000952430,
    polarization: 0.0,
    halfLife: "7,370 years",
  },
  {
    id: "np237",
    name: "Neptunium-237 (Np-237) [Actinide]",
    type: "alpha",
    baseEnergyMeV: 4.788,
    fluxIntensity: 22000,
    pdgId: 1000932370,
    polarization: 0.0,
    halfLife: "2.14 million years",
  },
  {
    id: "cm245",
    name: "Curium-245 (Cm-245) [Long-lived Isotope]",
    type: "alpha",
    baseEnergyMeV: 5.361,
    fluxIntensity: 18000,
    pdgId: 1000962450,
    polarization: 0.0,
    halfLife: "8,500 years",
  },
  {
    id: "cm246",
    name: "Curium-246 (Cm-246) [Fissile Isotope]",
    type: "alpha",
    baseEnergyMeV: 5.386,
    fluxIntensity: 15000,
    pdgId: 1000962460,
    polarization: 0.0,
    halfLife: "4,730 years",
  },
  {
    id: "bk247",
    name: "Berkelium-247 (Bk-247) [Transuranic]",
    type: "alpha",
    baseEnergyMeV: 5.900,
    fluxIntensity: 12000,
    pdgId: 1000972470,
    polarization: 0.0,
    halfLife: "1,380 years",
  },
  {
    id: "cf249",
    name: "Californium-249 (Cf-249) [Isotope]",
    type: "alpha",
    baseEnergyMeV: 5.876,
    fluxIntensity: 10000,
    pdgId: 1000982490,
    polarization: 0.0,
    halfLife: "351 years",
  },
  {
    id: "es252",
    name: "Einsteinium-252 (Es-252) [Synthetic]",
    type: "custom",
    baseEnergyMeV: 6.5,
    fluxIntensity: 5000,
    pdgId: 1000992520,
    polarization: 0.0,
    halfLife: "472 days",
  },
  {
    id: "fm257",
    name: "Fermium-257 (Fm-257) [Heaviest Natural]",
    type: "alpha",
    baseEnergyMeV: 6.8,
    fluxIntensity: 3000,
    pdgId: 1001002570,
    polarization: 0.0,
    halfLife: "100 days",
  },
  {
    id: "md258",
    name: "Mendelevium-258 (Md-258) [Synthetic]",
    type: "custom",
    baseEnergyMeV: 7.2,
    fluxIntensity: 2000,
    pdgId: 1001012580,
    polarization: 0.0,
    halfLife: "51 days",
  },
  {
    id: "no259",
    name: "Nobelium-259 (No-259) [Synthetic]",
    type: "alpha",
    baseEnergyMeV: 7.5,
    fluxIntensity: 1500,
    pdgId: 1001022590,
    polarization: 0.0,
    halfLife: "58 minutes",
  },
  {
    id: "lr262",
    name: "Lawrencium-262 (Lr-262) [Synthetic]",
    type: "custom",
    baseEnergyMeV: 8.0,
    fluxIntensity: 1000,
    pdgId: 1001032620,
    polarization: 0.0,
    halfLife: "3.6 hours",
  },
  {
    id: "rf267",
    name: "Rutherfordium-267 (Rf-267) [Synthetic]",
    type: "custom",
    baseEnergyMeV: 8.5,
    fluxIntensity: 800,
    pdgId: 1001042670,
    polarization: 0.0,
    halfLife: "1.3 hours",
  },
  {
    id: "db268",
    name: "Dubnium-268 (Db-268) [Synthetic]",
    type: "custom",
    baseEnergyMeV: 8.8,
    fluxIntensity: 600,
    pdgId: 1001052680,
    polarization: 0.0,
    halfLife: "28 hours",
  },
  {
    id: "sg271",
    name: "Seaborgium-271 (Sg-271) [Synthetic]",
    type: "custom",
    baseEnergyMeV: 9.2,
    fluxIntensity: 500,
    pdgId: 1001062710,
    polarization: 0.0,
    halfLife: "2.4 minutes",
  },
  {
    id: "bh277",
    name: "Bohrium-277 (Bh-277) [Synthetic]",
    type: "custom",
    baseEnergyMeV: 9.8,
    fluxIntensity: 400,
    pdgId: 1001072770,
    polarization: 0.0,
    halfLife: "9.3 minutes",
  },
  {
    id: "hs277",
    name: "Hassium-277 (Hs-277) [Synthetic]",
    type: "custom",
    baseEnergyMeV: 10.0,
    fluxIntensity: 350,
    pdgId: 1001082770,
    polarization: 0.0,
    halfLife: "12 minutes",
  },
  {
    id: "mt278",
    name: "Meitnerium-278 (Mt-278) [Synthetic]",
    type: "custom",
    baseEnergyMeV: 10.3,
    fluxIntensity: 300,
    pdgId: 1001092780,
    polarization: 0.0,
    halfLife: "7.6 seconds",
  },
  {
    id: "ds281",
    name: "Darmstadtium-281 (Ds-281) [Synthetic]",
    type: "custom",
    baseEnergyMeV: 10.7,
    fluxIntensity: 250,
    pdgId: 1001102810,
    polarization: 0.0,
    halfLife: "12.7 seconds",
  },
  {
    id: "rg282",
    name: "Roentgenium-282 (Rg-282) [Synthetic]",
    type: "custom",
    baseEnergyMeV: 11.0,
    fluxIntensity: 200,
    pdgId: 1001112820,
    polarization: 0.0,
    halfLife: "2.4 minutes",
  },
  {
    id: "cn285",
    name: "Copernicium-285 (Cn-285) [Synthetic]",
    type: "custom",
    baseEnergyMeV: 11.5,
    fluxIntensity: 150,
    pdgId: 1001122850,
    polarization: 0.0,
    halfLife: "29 seconds",
  },
  {
    id: "nh286",
    name: "Nihonium-286 (Nh-286) [Synthetic]",
    type: "custom",
    baseEnergyMeV: 11.8,
    fluxIntensity: 120,
    pdgId: 1001132860,
    polarization: 0.0,
    halfLife: "20 seconds",
  },
  {
    id: "fl289",
    name: "Flerovium-289 (Fl-289) [Synthetic]",
    type: "custom",
    baseEnergyMeV: 12.2,
    fluxIntensity: 100,
    pdgId: 1001142890,
    polarization: 0.0,
    halfLife: "2.6 seconds",
  },
  {
    id: "mc290",
    name: "Moscovium-290 (Mc-290) [Synthetic]",
    type: "custom",
    baseEnergyMeV: 12.5,
    fluxIntensity: 80,
    pdgId: 1001152900,
    polarization: 0.0,
    halfLife: "0.65 seconds",
  },
  {
    id: "lv293",
    name: "Livermorium-293 (Lv-293) [Synthetic]",
    type: "custom",
    baseEnergyMeV: 13.0,
    fluxIntensity: 60,
    pdgId: 1001162930,
    polarization: 0.0,
    halfLife: "0.061 seconds",
  },
  {
    id: "ts294",
    name: "Tennessine-294 (Ts-294) [Synthetic]",
    type: "custom",
    baseEnergyMeV: 13.5,
    fluxIntensity: 40,
    pdgId: 1001172940,
    polarization: 0.0,
    halfLife: "51 milliseconds",
  },
  {
    id: "og294",
    name: "Oganesson-294 (Og-294) [Synthetic]",
    type: "custom",
    baseEnergyMeV: 14.0,
    fluxIntensity: 20,
    pdgId: 1001182940,
    polarization: 0.0,
    halfLife: "0.7 milliseconds",
  },
  {
    id: "anti_proton",
    name: "Anti-Proton (p-) [Antimatter Beam]",
    type: "proton",
    baseEnergyMeV: 1200.0,
    fluxIntensity: 85000,
    pdgId: -2212,
    polarization: 0.5,
    halfLife: "Stable",
  },
  {
    id: "pion_plus",
    name: "Pion (π+) [Meson Factory Beam]",
    type: "meson",
    baseEnergyMeV: 300.0,
    fluxIntensity: 1500000,
    pdgId: 211,
    polarization: 0.0,
    halfLife: "26 nanoseconds",
  },
  {
    id: "o15",
    name: "Oxygen-15 (O-15) [PET Short-Lived]",
    type: "beta+",
    baseEnergyMeV: 1.732,
    fluxIntensity: 3500000,
    pdgId: 11,
    polarization: 0.0,
    halfLife: "122 seconds",
  },
  {
    id: "n13",
    name: "Nitrogen-13 (N-13) [Cardiac PET]",
    type: "beta+",
    baseEnergyMeV: 1.19,
    fluxIntensity: 2800000,
    pdgId: 11,
    polarization: 0.0,
    halfLife: "9.97 minutes",
  },
  {
    id: "c11",
    name: "Carbon-11 (C-11) [Neuro PET Imaging]",
    type: "beta+",
    baseEnergyMeV: 0.96,
    fluxIntensity: 2200000,
    pdgId: 11,
    polarization: 0.0,
    halfLife: "20.3 minutes",
  },
  {
    id: "ga68",
    name: "Gallium-68 (Ga-68) [Generator PET]",
    type: "beta+",
    baseEnergyMeV: 1.899,
    fluxIntensity: 4500000,
    pdgId: 11,
    polarization: 0.0,
    halfLife: "67.7 minutes",
  },
  {
    id: "y86",
    name: "Yttrium-86 (Y-86) [Theranostic PET]",
    type: "beta+",
    baseEnergyMeV: 1.22,
    fluxIntensity: 1500000,
    pdgId: 11,
    polarization: 0.0,
    halfLife: "14.7 hours",
  },
  {
    id: "zr89",
    name: "Zirconium-89 (Zr-89) [Immuno-PET]",
    type: "beta+",
    baseEnergyMeV: 0.395,
    fluxIntensity: 1200000,
    pdgId: 11,
    polarization: 0.0,
    halfLife: "78.4 hours",
  },
  {
    id: "cu64",
    name: "Copper-64 (Cu-64) [Theranostic]",
    type: "beta+",
    baseEnergyMeV: 0.653,
    fluxIntensity: 1800000,
    pdgId: 11,
    polarization: 0.0,
    halfLife: "12.7 hours",
  },
  {
    id: "th227",
    name: "Thorium-227 (Th-227) [Targeted Alpha]",
    type: "alpha",
    baseEnergyMeV: 5.9,
    fluxIntensity: 85000,
    pdgId: 1000902270,
    polarization: 0.0,
    halfLife: "18.7 days",
  },
  {
    id: "pb211",
    name: "Lead-211 (Pb-211) [Actinium Series]",
    type: "beta-",
    baseEnergyMeV: 1.37,
    fluxIntensity: 320000,
    pdgId: 1000822110,
    polarization: 0.0,
    halfLife: "36.1 minutes",
  },
  {
    id: "ti44",
    name: "Titanium-44 (Ti-44) [Supernova Isotope]",
    type: "ec",
    baseEnergyMeV: 0.068,
    fluxIntensity: 15000,
    pdgId: 1000220440,
    polarization: 0.0,
    halfLife: "60 years",
  }
];

export const MATERIAL_PRESETS: MaterialPreset[] = [
  {
    id: "heavy_water",
    name: "Heavy Water (D2O) [Reactor Active Core]",
    refractiveIndexStatic: 1.328,
    sellmeierCoeffs: { B1: 0.559, C1: 0.0049, B2: 0.203, C2: 0.0131 },
    mu_a_base: 0.0001,
    mu_s_base: 0.008,
    anisotropyG: 0.999,
  },
  {
    id: "lead_glass",
    name: "High-Density Lead Glass (SF5) [EM Calorimeter]",
    refractiveIndexStatic: 1.673,
    sellmeierCoeffs: { B1: 0.912, C1: 0.011, B2: 0.521, C2: 0.038 },
    mu_a_base: 0.005,
    mu_s_base: 0.015,
    anisotropyG: 0.985,
  },
  {
    id: "liquid_argon",
    name: "Liquid Argon (LAr) [Cryogenic Neutrino Detector]",
    refractiveIndexStatic: 1.233,
    sellmeierCoeffs: { B1: 0.412, C1: 0.0035, B2: 0.155, C2: 0.0098 },
    mu_a_base: 0.0002,
    mu_s_base: 0.045,
    anisotropyG: 0.95,
  },
  {
    id: "plastic_scintillator",
    name: "Polyvinyltoluene PMMA (BC-408) [Scintillation Tile]",
    refractiveIndexStatic: 1.582,
    sellmeierCoeffs: { B1: 0.805, C1: 0.0105, B2: 0.412, C2: 0.032 },
    mu_a_base: 0.025,
    mu_s_base: 0.12,
    anisotropyG: 0.91,
  },
  {
    id: "water",
    name: "Pure Liquid Water (H2O) [Standard Reference]",
    refractiveIndexStatic: 1.333,
    sellmeierCoeffs: { B1: 0.567, C1: 0.0051, B2: 0.208, C2: 0.0131 },
    mu_a_base: 0.00001,
    mu_s_base: 0.001,
    anisotropyG: 0.999,
  },
  {
    id: "quartz",
    name: "High-Purity Fused Silica (Quartz SiO2)",
    refractiveIndexStatic: 1.458,
    sellmeierCoeffs: { B1: 0.696, C1: 0.0046, B2: 0.407, C2: 0.0135 },
    mu_a_base: 1e-6,
    mu_s_base: 0.0002,
    anisotropyG: 0.999,
  },
  {
    id: "lyso",
    name: "Heavy LYSO Crystal (Lu2-xYxSiO5:Ce)",
    refractiveIndexStatic: 1.82,
    sellmeierCoeffs: { B1: 1.25, C1: 0.012, B2: 0.82, C2: 0.045 },
    mu_a_base: 0.001,
    mu_s_base: 0.01,
    anisotropyG: 0.96,
  },
  {
    id: "aerogel",
    name: "Silica Aerogel (n=1.050) [RICH Spectrometer Block]",
    refractiveIndexStatic: 1.050,
    sellmeierCoeffs: { B1: 0.096, C1: 0.0035, B2: 0.005, C2: 0.012 },
    mu_a_base: 1e-5,
    mu_s_base: 0.03,
    anisotropyG: 0.98,
  },
  {
    id: "bgo",
    name: "BGO Crystal (Bi4Ge3O12) [High-Z Scintillator]",
    refractiveIndexStatic: 2.15,
    sellmeierCoeffs: { B1: 1.85, C1: 0.018, B2: 1.12, C2: 0.058 },
    mu_a_base: 0.002,
    mu_s_base: 0.015,
    anisotropyG: 0.94,
  },
  {
    id: "sapphire",
    name: "Sapphire (Al2O3) [High-Temperature Window]",
    refractiveIndexStatic: 1.77,
    sellmeierCoeffs: { B1: 1.02, C1: 0.014, B2: 0.68, C2: 0.042 },
    mu_a_base: 0.0005,
    mu_s_base: 0.008,
    anisotropyG: 0.97,
  },
  {
    id: "diamond",
    name: "Synthetic Diamond (C) [Radiation Hard Detector]",
    refractiveIndexStatic: 2.42,
    sellmeierCoeffs: { B1: 2.35, C1: 0.022, B2: 1.45, C2: 0.075 },
    mu_a_base: 0.0001,
    mu_s_base: 0.005,
    anisotropyG: 0.99,
  },
  {
    id: "tissue",
    name: "Biological Tissue (Soft Tissue) [Medical Dosimetry]",
    refractiveIndexStatic: 1.38,
    sellmeierCoeffs: { B1: 0.62, C1: 0.0058, B2: 0.25, C2: 0.0145 },
    mu_a_base: 0.08,
    mu_s_base: 15.0,
    anisotropyG: 0.90,
  },
  {
    id: "bone",
    name: "Cortical Bone [Medical Dosimetry]",
    refractiveIndexStatic: 1.55,
    sellmeierCoeffs: { B1: 0.78, C1: 0.0095, B2: 0.38, C2: 0.028 },
    mu_a_base: 0.15,
    mu_s_base: 25.0,
    anisotropyG: 0.88,
  },
  {
    id: "acrylic",
    name: "PMMA Acrylic (Perspex) [Optical Window]",
    refractiveIndexStatic: 1.49,
    sellmeierCoeffs: { B1: 0.72, C1: 0.0082, B2: 0.41, C2: 0.024 },
    mu_a_base: 0.001,
    mu_s_base: 0.05,
    anisotropyG: 0.96,
  },
  {
    id: "polystyrene",
    name: "Polystyrene (PS) [Scintillator Base]",
    refractiveIndexStatic: 1.59,
    sellmeierCoeffs: { B1: 0.81, C1: 0.0108, B2: 0.42, C2: 0.033 },
    mu_a_base: 0.008,
    mu_s_base: 0.1,
    anisotropyG: 0.92,
  },
  {
    id: "glass_bk7",
    name: "BK7 Optical Glass [Lens Material]",
    refractiveIndexStatic: 1.517,
    sellmeierCoeffs: { B1: 1.0396, C1: 0.006, B2: 0.2318, C2: 0.02 },
    mu_a_base: 0.0005,
    mu_s_base: 0.003,
    anisotropyG: 0.99,
  },
  {
    id: "glass_sf10",
    name: "SF10 High-Index Glass [Prism Material]",
    refractiveIndexStatic: 1.728,
    sellmeierCoeffs: { B1: 1.6215, C1: 0.0122, B2: 0.2563, C2: 0.04 },
    mu_a_base: 0.002,
    mu_s_base: 0.008,
    anisotropyG: 0.97,
  },
  {
    id: "silicon",
    name: "Crystalline Silicon (Si) [Semiconductor]",
    refractiveIndexStatic: 3.42,
    sellmeierCoeffs: { B1: 3.5, C1: 0.025, B2: 2.1, C2: 0.08 },
    mu_a_base: 0.01,
    mu_s_base: 0.02,
    anisotropyG: 0.95,
  },
  {
    id: "germanium",
    name: "Germanium (Ge) [IR Detector]",
    refractiveIndexStatic: 4.0,
    sellmeierCoeffs: { B1: 4.2, C1: 0.03, B2: 2.5, C2: 0.09 },
    mu_a_base: 0.02,
    mu_s_base: 0.03,
    anisotropyG: 0.94,
  },
  {
    id: "zinc_selenide",
    name: "Zinc Selenide (ZnSe) [IR Window]",
    refractiveIndexStatic: 2.4,
    sellmeierCoeffs: { B1: 2.8, C1: 0.02, B2: 1.8, C2: 0.06 },
    mu_a_base: 0.003,
    mu_s_base: 0.01,
    anisotropyG: 0.96,
  },
  {
    id: "calcium_fluoride",
    name: "Calcium Fluoride (CaF2) [UV Window]",
    refractiveIndexStatic: 1.434,
    sellmeierCoeffs: { B1: 0.567, C1: 0.005, B2: 0.471, C2: 0.01 },
    mu_a_base: 0.0002,
    mu_s_base: 0.005,
    anisotropyG: 0.98,
  },
  {
    id: "magnesium_fluoride",
    name: "Magnesium Fluoride (MgF2) [Deep UV]",
    refractiveIndexStatic: 1.378,
    sellmeierCoeffs: { B1: 0.487, C1: 0.0043, B2: 0.398, C2: 0.009 },
    mu_a_base: 0.0001,
    mu_s_base: 0.004,
    anisotropyG: 0.99,
  },
  {
    id: "oil_immersion",
    name: "Microscope Oil (n=1.515) [Immersion]",
    refractiveIndexStatic: 1.515,
    sellmeierCoeffs: { B1: 0.68, C1: 0.0075, B2: 0.39, C2: 0.022 },
    mu_a_base: 0.0005,
    mu_s_base: 0.002,
    anisotropyG: 0.99,
  },
  {
    id: "air",
    name: "Standard Air (STP) [Reference Medium]",
    refractiveIndexStatic: 1.000293,
    sellmeierCoeffs: { B1: 0.000057, C1: 0.005, B2: 0.000032, C2: 0.014 },
    mu_a_base: 0.000001,
    mu_s_base: 0.00001,
    anisotropyG: 0.999,
  },
  {
    id: "lithium_fluoride",
    name: "Lithium Fluoride (LiF) [UV Window]",
    refractiveIndexStatic: 1.392,
    sellmeierCoeffs: { B1: 0.489, C1: 0.0042, B2: 0.398, C2: 0.0095 },
    mu_a_base: 0.0003,
    mu_s_base: 0.006,
    anisotropyG: 0.98,
  },
  {
    id: "barium_fluoride",
    name: "Barium Fluoride (BaF2) [Fast Scintillator]",
    refractiveIndexStatic: 1.474,
    sellmeierCoeffs: { B1: 0.634, C1: 0.0068, B2: 0.512, C2: 0.0155 },
    mu_a_base: 0.0008,
    mu_s_base: 0.012,
    anisotropyG: 0.96,
  },
  {
    id: "cesium_iodide",
    name: "Cesium Iodide (CsI) [Scintillator]",
    refractiveIndexStatic: 1.739,
    sellmeierCoeffs: { B1: 0.845, C1: 0.0095, B2: 0.623, C2: 0.028 },
    mu_a_base: 0.0015,
    mu_s_base: 0.018,
    anisotropyG: 0.94,
  },
  {
    id: "sodium_iodide",
    name: "Sodium Iodide (NaI) [Gamma Detector]",
    refractiveIndexStatic: 1.77,
    sellmeierCoeffs: { B1: 0.876, C1: 0.0102, B2: 0.654, C2: 0.031 },
    mu_a_base: 0.002,
    mu_s_base: 0.02,
    anisotropyG: 0.93,
  },
  {
    id: "lead_tungstate",
    name: "Lead Tungstate (PWO) [EM Calorimeter]",
    refractiveIndexStatic: 2.16,
    sellmeierCoeffs: { B1: 1.92, C1: 0.019, B2: 1.15, C2: 0.059 },
    mu_a_base: 0.003,
    mu_s_base: 0.025,
    anisotropyG: 0.91,
  },
  {
    id: "cerium_fluoride",
    name: "Cerium Fluoride (CeF3) [Fast Scintillator]",
    refractiveIndexStatic: 1.62,
    sellmeierCoeffs: { B1: 0.734, C1: 0.0089, B2: 0.489, C2: 0.025 },
    mu_a_base: 0.0012,
    mu_s_base: 0.014,
    anisotropyG: 0.95,
  },
  {
    id: "lutetium_yttrium_oxyorthosilicate",
    name: "LuYAP [Mixed Scintillator]",
    refractiveIndexStatic: 1.78,
    sellmeierCoeffs: { B1: 1.12, C1: 0.013, B2: 0.78, C2: 0.042 },
    mu_a_base: 0.0015,
    mu_s_base: 0.016,
    anisotropyG: 0.94,
  },
  {
    id: "plastic_scintillator_ej200",
    name: "EJ-200 Plastic [Fast Timing]",
    refractiveIndexStatic: 1.58,
    sellmeierCoeffs: { B1: 0.795, C1: 0.0104, B2: 0.408, C2: 0.032 },
    mu_a_base: 0.02,
    mu_s_base: 0.11,
    anisotropyG: 0.90,
  },
  {
    id: "liquid_scintillator",
    name: "Liquid Scintillator [Neutrino Detector]",
    refractiveIndexStatic: 1.49,
    sellmeierCoeffs: { B1: 0.685, C1: 0.0081, B2: 0.395, C2: 0.024 },
    mu_a_base: 0.015,
    mu_s_base: 0.08,
    anisotropyG: 0.92,
  },
  {
    id: "xenon_liquid",
    name: "Liquid Xenon (LXe) [Dark Matter Detector]",
    refractiveIndexStatic: 1.36,
    sellmeierCoeffs: { B1: 0.545, C1: 0.0048, B2: 0.245, C2: 0.0128 },
    mu_a_base: 0.0003,
    mu_s_base: 0.055,
    anisotropyG: 0.94,
  },
  {
    id: "germanium_bicarbonate",
    name: "Germanium Bicarbonate [Novel Scintillator]",
    refractiveIndexStatic: 1.65,
    sellmeierCoeffs: { B1: 0.812, C1: 0.0098, B2: 0.523, C2: 0.029 },
    mu_a_base: 0.0018,
    mu_s_base: 0.019,
    anisotropyG: 0.93,
  },
  {
    id: "zinc_tungstate",
    name: "Zinc Tungstate (ZnWO4) [Crystal Scintillator]",
    refractiveIndexStatic: 2.18,
    sellmeierCoeffs: { B1: 1.95, C1: 0.0195, B2: 1.18, C2: 0.060 },
    mu_a_base: 0.0035,
    mu_s_base: 0.028,
    anisotropyG: 0.90,
  },
  {
    id: "cadmium_tungstate",
    name: "Cadmium Tungstate (CdWO4) [Heavy Scintillator]",
    refractiveIndexStatic: 2.20,
    sellmeierCoeffs: { B1: 2.02, C1: 0.020, B2: 1.22, C2: 0.062 },
    mu_a_base: 0.004,
    mu_s_base: 0.030,
    anisotropyG: 0.89,
  },
  {
    id: "gagg",
    name: "GAGG Crystal (Gd3Al2Ga3O12:Ce) [Fast Scintillator]",
    refractiveIndexStatic: 1.92,
    sellmeierCoeffs: { B1: 1.45, C1: 0.015, B2: 0.95, C2: 0.048 },
    mu_a_base: 0.0012,
    mu_s_base: 0.013,
    anisotropyG: 0.95,
  },
  {
    id: "yag",
    name: "YAG Crystal (Y3Al5O12:Ce) [Laser Medium]",
    refractiveIndexStatic: 1.82,
    sellmeierCoeffs: { B1: 1.25, C1: 0.012, B2: 0.82, C2: 0.045 },
    mu_a_base: 0.0008,
    mu_s_base: 0.009,
    anisotropyG: 0.96,
  },
  {
    id: "luag",
    name: "LuAG Crystal (Lu3Al5O12:Ce) [High-Density Scintillator]",
    refractiveIndexStatic: 1.84,
    sellmeierCoeffs: { B1: 1.28, C1: 0.0125, B2: 0.85, C2: 0.046 },
    mu_a_base: 0.001,
    mu_s_base: 0.011,
    anisotropyG: 0.95,
  },
  {
    id: "yap",
    name: "YAP Crystal (YAlO3:Ce) [Fast Response]",
    refractiveIndexStatic: 1.95,
    sellmeierCoeffs: { B1: 1.42, C1: 0.0145, B2: 0.92, C2: 0.049 },
    mu_a_base: 0.0015,
    mu_s_base: 0.014,
    anisotropyG: 0.94,
  },
  {
    id: "lso",
    name: "LSO Crystal (Lu2SiO5:Ce) [PET Detector]",
    refractiveIndexStatic: 1.82,
    sellmeierCoeffs: { B1: 1.23, C1: 0.0118, B2: 0.81, C2: 0.044 },
    mu_a_base: 0.0009,
    mu_s_base: 0.010,
    anisotropyG: 0.96,
  },
  {
    id: "gso",
    name: "GSO Crystal (Gd2SiO5:Ce) [High-Light Yield]",
    refractiveIndexStatic: 1.85,
    sellmeierCoeffs: { B1: 1.31, C1: 0.013, B2: 0.88, C2: 0.047 },
    mu_a_base: 0.0011,
    mu_s_base: 0.012,
    anisotropyG: 0.95,
  },
  {
    id: "cso",
    name: "CSO Crystal (CaSO4:Ce) [UV Scintillator]",
    refractiveIndexStatic: 1.59,
    sellmeierCoeffs: { B1: 0.88, C1: 0.0105, B2: 0.52, C2: 0.030 },
    mu_a_base: 0.0025,
    mu_s_base: 0.022,
    anisotropyG: 0.92,
  },
  {
    id: "zag",
    name: "ZAG Crystal (ZnAl2O4:Ga) [UV-Blue Emitter]",
    refractiveIndexStatic: 1.92,
    sellmeierCoeffs: { B1: 1.44, C1: 0.0148, B2: 0.93, C2: 0.049 },
    mu_a_base: 0.0013,
    mu_s_base: 0.014,
    anisotropyG: 0.94,
  },
  {
    id: "gas",
    name: "GAS Crystal (Gd2O2S:Tb) [X-ray Screen]",
    refractiveIndexStatic: 2.28,
    sellmeierCoeffs: { B1: 1.98, C1: 0.019, B2: 1.20, C2: 0.061 },
    mu_a_base: 0.0038,
    mu_s_base: 0.032,
    anisotropyG: 0.88,
  },
  {
    id: "zag_crystal",
    name: "ZAG Crystal (ZnGa2O4:Cr) [NIR Emitter]",
    refractiveIndexStatic: 2.05,
    sellmeierCoeffs: { B1: 1.68, C1: 0.0165, B2: 1.05, C2: 0.052 },
    mu_a_base: 0.0018,
    mu_s_base: 0.017,
    anisotropyG: 0.93,
  },
  {
    id: "mgo",
    name: "MgO Crystal (Periclase) [IR Window]",
    refractiveIndexStatic: 1.74,
    sellmeierCoeffs: { B1: 1.15, C1: 0.0135, B2: 0.72, C2: 0.041 },
    mu_a_base: 0.0006,
    mu_s_base: 0.007,
    anisotropyG: 0.97,
  },
  {
    id: "caf2_crystal",
    name: "CaF2 Crystal [UV-IR Window]",
    refractiveIndexStatic: 1.434,
    sellmeierCoeffs: { B1: 0.567, C1: 0.005, B2: 0.471, C2: 0.01 },
    mu_a_base: 0.0002,
    mu_s_base: 0.005,
    anisotropyG: 0.98,
  },
  {
    id: "baf2_crystal",
    name: "BaF2 Crystal [Fast Scintillator]",
    refractiveIndexStatic: 1.474,
    sellmeierCoeffs: { B1: 0.634, C1: 0.0068, B2: 0.512, C2: 0.0155 },
    mu_a_base: 0.0008,
    mu_s_base: 0.012,
    anisotropyG: 0.96,
  },
  {
    id: "pbf2",
    name: "PbF2 Crystal [High-Z Scintillator]",
    refractiveIndexStatic: 1.82,
    sellmeierCoeffs: { B1: 1.25, C1: 0.012, B2: 0.82, C2: 0.045 },
    mu_a_base: 0.0022,
    mu_s_base: 0.018,
    anisotropyG: 0.93,
  },
  {
    id: "cdf2",
    name: "CdF2 Crystal [UV Detector]",
    refractiveIndexStatic: 1.55,
    sellmeierCoeffs: { B1: 0.82, C1: 0.0098, B2: 0.48, C2: 0.027 },
    mu_a_base: 0.0015,
    mu_s_base: 0.015,
    anisotropyG: 0.94,
  },
  {
    id: "srf2",
    name: "SrF2 Crystal [UV Window]",
    refractiveIndexStatic: 1.44,
    sellmeierCoeffs: { B1: 0.58, C1: 0.0052, B2: 0.48, C2: 0.0102 },
    mu_a_base: 0.0003,
    mu_s_base: 0.006,
    anisotropyG: 0.97,
  },
  {
    id: "kaf2",
    name: "KAF2 Crystal (KAlF4) [Novel UV Material]",
    refractiveIndexStatic: 1.38,
    sellmeierCoeffs: { B1: 0.52, C1: 0.0048, B2: 0.42, C2: 0.0095 },
    mu_a_base: 0.0004,
    mu_s_base: 0.007,
    anisotropyG: 0.97,
  },
  {
    id: "lif_crystal",
    name: "LiF Crystal [VUV Window]",
    refractiveIndexStatic: 1.392,
    sellmeierCoeffs: { B1: 0.489, C1: 0.0042, B2: 0.398, C2: 0.0095 },
    mu_a_base: 0.0003,
    mu_s_base: 0.006,
    anisotropyG: 0.98,
  },
  {
    id: "naf",
    name: "NaF Crystal [IR Material]",
    refractiveIndexStatic: 1.33,
    sellmeierCoeffs: { B1: 0.48, C1: 0.0045, B2: 0.38, C2: 0.009 },
    mu_a_base: 0.0002,
    mu_s_base: 0.005,
    anisotropyG: 0.98,
  },
  {
    id: "kcl",
    name: "KCl Crystal [IR Window]",
    refractiveIndexStatic: 1.49,
    sellmeierCoeffs: { B1: 0.72, C1: 0.008, B2: 0.42, C2: 0.024 },
    mu_a_base: 0.0005,
    mu_s_base: 0.006,
    anisotropyG: 0.97,
  },
  {
    id: "nacl",
    name: "NaCl Crystal (Rock Salt) [IR Window]",
    refractiveIndexStatic: 1.54,
    sellmeierCoeffs: { B1: 0.78, C1: 0.009, B2: 0.45, C2: 0.026 },
    mu_a_base: 0.0006,
    mu_s_base: 0.007,
    anisotropyG: 0.96,
  },
  {
    id: "kbr",
    name: "KBr Crystal [IR Window]",
    refractiveIndexStatic: 1.56,
    sellmeierCoeffs: { B1: 0.81, C1: 0.0095, B2: 0.47, C2: 0.027 },
    mu_a_base: 0.0007,
    mu_s_base: 0.008,
    anisotropyG: 0.96,
  },
  {
    id: "csi_crystal",
    name: "CsI Crystal [Scintillator]",
    refractiveIndexStatic: 1.739,
    sellmeierCoeffs: { B1: 0.845, C1: 0.0095, B2: 0.623, C2: 0.028 },
    mu_a_base: 0.0015,
    mu_s_base: 0.018,
    anisotropyG: 0.94,
  },
  {
    id: "nai_crystal",
    name: "NaI Crystal [Gamma Detector]",
    refractiveIndexStatic: 1.77,
    sellmeierCoeffs: { B1: 0.876, C1: 0.0102, B2: 0.654, C2: 0.031 },
    mu_a_base: 0.002,
    mu_s_base: 0.02,
    anisotropyG: 0.93,
  },
  {
    id: "tibi",
    name: "TlBr Crystal (Thallium Bromide) [Gamma Detector]",
    refractiveIndexStatic: 2.70,
    sellmeierCoeffs: { B1: 2.45, C1: 0.024, B2: 1.55, C2: 0.078 },
    mu_a_base: 0.005,
    mu_s_base: 0.035,
    anisotropyG: 0.88,
  },
  {
    id: "cdte",
    name: "CdTe Crystal (Cadmium Telluride) [X-ray Detector]",
    refractiveIndexStatic: 2.68,
    sellmeierCoeffs: { B1: 2.42, C1: 0.0235, B2: 1.52, C2: 0.077 },
    mu_a_base: 0.0048,
    mu_s_base: 0.033,
    anisotropyG: 0.89,
  },
  {
    id: "cdznte",
    name: "CZT Crystal (CdZnTe) [Room-Temp Detector]",
    refractiveIndexStatic: 2.65,
    sellmeierCoeffs: { B1: 2.38, C1: 0.023, B2: 1.48, C2: 0.076 },
    mu_a_base: 0.0045,
    mu_s_base: 0.032,
    anisotropyG: 0.89,
  },
  {
    id: "hgai2te4",
    name: "MCT Crystal (HgCdTe) [IR Focal Plane]",
    refractiveIndexStatic: 3.55,
    sellmeierCoeffs: { B1: 3.2, C1: 0.028, B2: 2.0, C2: 0.085 },
    mu_a_base: 0.025,
    mu_s_base: 0.04,
    anisotropyG: 0.85,
  },
  {
    id: "inas",
    name: "InAs Crystal (Indium Arsenide) [IR Detector]",
    refractiveIndexStatic: 3.52,
    sellmeierCoeffs: { B1: 3.15, C1: 0.0275, B2: 1.98, C2: 0.084 },
    mu_a_base: 0.024,
    mu_s_base: 0.039,
    anisotropyG: 0.86,
  },
  {
    id: "inp",
    name: "InP Crystal (Indium Phosphide) [Photonic]",
    refractiveIndexStatic: 3.55,
    sellmeierCoeffs: { B1: 3.22, C1: 0.0282, B2: 2.02, C2: 0.085 },
    mu_a_base: 0.023,
    mu_s_base: 0.038,
    anisotropyG: 0.86,
  },
  {
    id: "gaas",
    name: "GaAs Crystal (Gallium Arsenide) [Optoelectronic]",
    refractiveIndexStatic: 3.3,
    sellmeierCoeffs: { B1: 2.95, C1: 0.026, B2: 1.85, C2: 0.082 },
    mu_a_base: 0.02,
    mu_s_base: 0.035,
    anisotropyG: 0.87,
  },
  {
    id: "gap",
    name: "GaP Crystal (Gallium Phosphide) [LED Material]",
    refractiveIndexStatic: 3.18,
    sellmeierCoeffs: { B1: 2.85, C1: 0.0255, B2: 1.78, C2: 0.081 },
    mu_a_base: 0.019,
    mu_s_base: 0.034,
    anisotropyG: 0.87,
  },
  {
    id: "sic",
    name: "SiC Crystal (Silicon Carbide) [Wide Bandgap]",
    refractiveIndexStatic: 2.65,
    sellmeierCoeffs: { B1: 2.38, C1: 0.023, B2: 1.48, C2: 0.076 },
    mu_a_base: 0.018,
    mu_s_base: 0.032,
    anisotropyG: 0.88,
  },
  {
    id: "gan",
    name: "GaN Crystal (Gallium Nitride) [UV LED]",
    refractiveIndexStatic: 2.42,
    sellmeierCoeffs: { B1: 2.15, C1: 0.021, B2: 1.35, C2: 0.072 },
    mu_a_base: 0.015,
    mu_s_base: 0.028,
    anisotropyG: 0.89,
  },
  {
    id: "aln",
    name: "AlN Crystal (Aluminum Nitride) [Deep UV]",
    refractiveIndexStatic: 2.15,
    sellmeierCoeffs: { B1: 1.88, C1: 0.019, B2: 1.18, C2: 0.065 },
    mu_a_base: 0.012,
    mu_s_base: 0.025,
    anisotropyG: 0.90,
  },
  {
    id: "zno",
    name: "ZnO Crystal (Zinc Oxide) [UV Material]",
    refractiveIndexStatic: 2.01,
    sellmeierCoeffs: { B1: 1.72, C1: 0.0175, B2: 1.08, C2: 0.058 },
    mu_a_base: 0.01,
    mu_s_base: 0.022,
    anisotropyG: 0.91,
  },
  {
    id: "tio2",
    name: "TiO2 Crystal (Rutile) [High-Index]",
    refractiveIndexStatic: 2.88,
    sellmeierCoeffs: { B1: 2.62, C1: 0.025, B2: 1.65, C2: 0.080 },
    mu_a_base: 0.006,
    mu_s_base: 0.038,
    anisotropyG: 0.88,
  },
  {
    id: "zro2",
    name: "ZrO2 Crystal (Zirconia) [Ceramic]",
    refractiveIndexStatic: 2.17,
    sellmeierCoeffs: { B1: 1.92, C1: 0.019, B2: 1.20, C2: 0.062 },
    mu_a_base: 0.0035,
    mu_s_base: 0.025,
    anisotropyG: 0.91,
  },
  {
    id: "al2o3_crystal",
    name: "Al2O3 Crystal (Sapphire) [Optical Window]",
    refractiveIndexStatic: 1.77,
    sellmeierCoeffs: { B1: 1.02, C1: 0.014, B2: 0.68, C2: 0.042 },
    mu_a_base: 0.0005,
    mu_s_base: 0.008,
    anisotropyG: 0.97,
  },
  {
    id: "mgo_al2o3",
    name: "Spinel Crystal (MgAl2O4) [IR Window]",
    refractiveIndexStatic: 1.72,
    sellmeierCoeffs: { B1: 1.12, C1: 0.0135, B2: 0.70, C2: 0.041 },
    mu_a_base: 0.0006,
    mu_s_base: 0.008,
    anisotropyG: 0.97,
  },
  {
    id: "forsterite",
    name: "Forsterite Crystal (Mg2SiO4) [Geological]",
    refractiveIndexStatic: 1.64,
    sellmeierCoeffs: { B1: 0.95, C1: 0.011, B2: 0.58, C2: 0.034 },
    mu_a_base: 0.0008,
    mu_s_base: 0.009,
    anisotropyG: 0.96,
  },
  {
    id: "fayalite",
    name: "Fayalite Crystal (Fe2SiO4) [Geological]",
    refractiveIndexStatic: 1.82,
    sellmeierCoeffs: { B1: 1.28, C1: 0.0125, B2: 0.85, C2: 0.046 },
    mu_a_base: 0.0012,
    mu_s_base: 0.013,
    anisotropyG: 0.94,
  },
  {
    id: "teflon",
    name: "Teflon (PTFE) [Low-Loss Material]",
    refractiveIndexStatic: 1.35,
    sellmeierCoeffs: { B1: 0.52, C1: 0.0048, B2: 0.38, C2: 0.0092 },
    mu_a_base: 0.0001,
    mu_s_base: 0.002,
    anisotropyG: 0.99,
  },
  {
    id: "teflon_af",
    name: "Teflon AF [Amorphous Fluoropolymer]",
    refractiveIndexStatic: 1.30,
    sellmeierCoeffs: { B1: 0.48, C1: 0.0045, B2: 0.35, C2: 0.0088 },
    mu_a_base: 0.00008,
    mu_s_base: 0.0015,
    anisotropyG: 0.99,
  },
  {
    id: "fep",
    name: "FEP (Fluorinated Ethylene) [Cable Insulation]",
    refractiveIndexStatic: 1.34,
    sellmeierCoeffs: { B1: 0.50, C1: 0.0046, B2: 0.37, C2: 0.009 },
    mu_a_base: 0.00012,
    mu_s_base: 0.0018,
    anisotropyG: 0.99,
  },
  {
    id: "pfa",
    name: "PFA (Perfluoroalkoxy) [Chemical Resistant]",
    refractiveIndexStatic: 1.34,
    sellmeierCoeffs: { B1: 0.50, C1: 0.0046, B2: 0.37, C2: 0.009 },
    mu_a_base: 0.00012,
    mu_s_base: 0.0018,
    anisotropyG: 0.99,
  },
  {
    id: "etfe",
    name: "ETFE (Ethylene Tetrafluoroethylene) [Wire Coating]",
    refractiveIndexStatic: 1.40,
    sellmeierCoeffs: { B1: 0.55, C1: 0.005, B2: 0.40, C2: 0.0095 },
    mu_a_base: 0.00015,
    mu_s_base: 0.002,
    anisotropyG: 0.99,
  },
  {
    id: "ectfe",
    name: "ECTFE (Ethylene Chlorotrifluoroethylene) [Liner]",
    refractiveIndexStatic: 1.44,
    sellmeierCoeffs: { B1: 0.58, C1: 0.0052, B2: 0.42, C2: 0.010 },
    mu_a_base: 0.00018,
    mu_s_base: 0.0022,
    anisotropyG: 0.99,
  },
  {
    id: "pvdf",
    name: "PVDF (Polyvinylidene Fluoride) [Piezoelectric]",
    refractiveIndexStatic: 1.42,
    sellmeierCoeffs: { B1: 0.56, C1: 0.0051, B2: 0.41, C2: 0.0098 },
    mu_a_base: 0.0002,
    mu_s_base: 0.0025,
    anisotropyG: 0.98,
  },
  {
    id: "peek",
    name: "PEEK (Polyetheretherketone) [High-Temp Polymer]",
    refractiveIndexStatic: 1.65,
    sellmeierCoeffs: { B1: 0.92, C1: 0.0105, B2: 0.55, C2: 0.032 },
    mu_a_base: 0.0008,
    mu_s_base: 0.006,
    anisotropyG: 0.97,
  },
  {
    id: "polycarbonate",
    name: "Polycarbonate (PC) [Optical Lens]",
    refractiveIndexStatic: 1.58,
    sellmeierCoeffs: { B1: 0.88, C1: 0.0102, B2: 0.52, C2: 0.030 },
    mu_a_base: 0.0005,
    mu_s_base: 0.004,
    anisotropyG: 0.98,
  },
  {
    id: "pmma",
    name: "PMMA (Acrylic) [Optical Grade]",
    refractiveIndexStatic: 1.49,
    sellmeierCoeffs: { B1: 0.72, C1: 0.0082, B2: 0.41, C2: 0.024 },
    mu_a_base: 0.001,
    mu_s_base: 0.05,
    anisotropyG: 0.96,
  },
  {
    id: "polycarbonate_optical",
    name: "Optical Polycarbonate [Lens Blanks]",
    refractiveIndexStatic: 1.58,
    sellmeierCoeffs: { B1: 0.88, C1: 0.0102, B2: 0.52, C2: 0.030 },
    mu_a_base: 0.0005,
    mu_s_base: 0.004,
    anisotropyG: 0.98,
  },
  {
    id: "polymethylpentene",
    name: "PMP (Polymethylpentene) [Optical Window]",
    refractiveIndexStatic: 1.46,
    sellmeierCoeffs: { B1: 0.65, C1: 0.0075, B2: 0.38, C2: 0.023 },
    mu_a_base: 0.0003,
    mu_s_base: 0.003,
    anisotropyG: 0.98,
  },
  {
    id: "cyclic_olefin_copolymer",
    name: "COC (Cyclic Olefin Copolymer) [Optical]",
    refractiveIndexStatic: 1.53,
    sellmeierCoeffs: { B1: 0.75, C1: 0.0088, B2: 0.44, C2: 0.026 },
    mu_a_base: 0.0004,
    mu_s_base: 0.0035,
    anisotropyG: 0.98,
  },
  {
    id: "cyclic_olefin_polymer",
    name: "COP (Cyclic Olefin Polymer) [Medical]",
    refractiveIndexStatic: 1.53,
    sellmeierCoeffs: { B1: 0.75, C1: 0.0088, B2: 0.44, C2: 0.026 },
    mu_a_base: 0.0004,
    mu_s_base: 0.0035,
    anisotropyG: 0.98,
  },
  {
    id: " polystyrene_foam",
    name: "Polystyrene Foam [Insulation]",
    refractiveIndexStatic: 1.03,
    sellmeierCoeffs: { B1: 0.08, C1: 0.003, B2: 0.02, C2: 0.008 },
    mu_a_base: 0.00005,
    mu_s_base: 0.0008,
    anisotropyG: 0.999,
  },
  {
    id: "polyurethane_foam",
    name: "Polyurethane Foam [Insulation]",
    refractiveIndexStatic: 1.05,
    sellmeierCoeffs: { B1: 0.09, C1: 0.0032, B2: 0.022, C2: 0.0082 },
    mu_a_base: 0.00006,
    mu_s_base: 0.0009,
    anisotropyG: 0.999,
  },
  {
    id: "silicone",
    name: "Silicone Rubber [Sealant]",
    refractiveIndexStatic: 1.40,
    sellmeierCoeffs: { B1: 0.55, C1: 0.005, B2: 0.40, C2: 0.0095 },
    mu_a_base: 0.0002,
    mu_s_base: 0.002,
    anisotropyG: 0.98,
  },
  {
    id: "silicone_gel",
    name: "Silicone Gel [Encapsulant]",
    refractiveIndexStatic: 1.41,
    sellmeierCoeffs: { B1: 0.56, C1: 0.0051, B2: 0.41, C2: 0.0097 },
    mu_a_base: 0.0002,
    mu_s_base: 0.002,
    anisotropyG: 0.98,
  },
  {
    id: "epoxy",
    name: "Epoxy Resin [Encapsulant]",
    refractiveIndexStatic: 1.55,
    sellmeierCoeffs: { B1: 0.82, C1: 0.0095, B2: 0.48, C2: 0.028 },
    mu_a_base: 0.0006,
    mu_s_base: 0.005,
    anisotropyG: 0.97,
  },
  {
    id: "epoxy_optical",
    name: "Optical Epoxy [Lens Bonding]",
    refractiveIndexStatic: 1.54,
    sellmeierCoeffs: { B1: 0.80, C1: 0.0093, B2: 0.47, C2: 0.0275 },
    mu_a_base: 0.0005,
    mu_s_base: 0.0045,
    anisotropyG: 0.97,
  },
  {
    id: "uv_curable",
    name: "UV-Curable Adhesive [Optical Bonding]",
    refractiveIndexStatic: 1.52,
    sellmeierCoeffs: { B1: 0.77, C1: 0.009, B2: 0.45, C2: 0.0265 },
    mu_a_base: 0.0004,
    mu_s_base: 0.004,
    anisotropyG: 0.98,
  },
  {
    id: "silicone_oil",
    name: "Silicone Oil [Immersion]",
    refractiveIndexStatic: 1.40,
    sellmeierCoeffs: { B1: 0.55, C1: 0.005, B2: 0.40, C2: 0.0095 },
    mu_a_base: 0.0002,
    mu_s_base: 0.002,
    anisotropyG: 0.98,
  },
  {
    id: "mineral_oil",
    name: "Mineral Oil [Transformer]",
    refractiveIndexStatic: 1.47,
    sellmeierCoeffs: { B1: 0.68, C1: 0.0078, B2: 0.40, C2: 0.0235 },
    mu_a_base: 0.0003,
    mu_s_base: 0.003,
    anisotropyG: 0.98,
  },
  {
    id: "silicone_fluid",
    name: "Silicone Fluid [Heat Transfer]",
    refractiveIndexStatic: 1.40,
    sellmeierCoeffs: { B1: 0.55, C1: 0.005, B2: 0.40, C2: 0.0095 },
    mu_a_base: 0.0002,
    mu_s_base: 0.002,
    anisotropyG: 0.98,
  },
  {
    id: "glycol",
    name: "Ethylene Glycol [Coolant]",
    refractiveIndexStatic: 1.43,
    sellmeierCoeffs: { B1: 0.60, C1: 0.0055, B2: 0.42, C2: 0.0102 },
    mu_a_base: 0.00025,
    mu_s_base: 0.0025,
    anisotropyG: 0.98,
  },
  {
    id: "glycerol",
    name: "Glycerol (Glycerine) [Humectant]",
    refractiveIndexStatic: 1.47,
    sellmeierCoeffs: { B1: 0.68, C1: 0.0078, B2: 0.40, C2: 0.0235 },
    mu_a_base: 0.0003,
    mu_s_base: 0.003,
    anisotropyG: 0.98,
  },
  {
    id: "ethanol",
    name: "Ethanol (C2H5OH) [Solvent]",
    refractiveIndexStatic: 1.36,
    sellmeierCoeffs: { B1: 0.54, C1: 0.0049, B2: 0.37, C2: 0.0092 },
    mu_a_base: 0.00015,
    mu_s_base: 0.0015,
    anisotropyG: 0.99,
  },
  {
    id: "methanol",
    name: "Methanol (CH3OH) [Solvent]",
    refractiveIndexStatic: 1.33,
    sellmeierCoeffs: { B1: 0.50, C1: 0.0046, B2: 0.35, C2: 0.0088 },
    mu_a_base: 0.00012,
    mu_s_base: 0.0012,
    anisotropyG: 0.99,
  },
  {
    id: "isopropanol",
    name: "Isopropanol (IPA) [Cleaning]",
    refractiveIndexStatic: 1.38,
    sellmeierCoeffs: { B1: 0.55, C1: 0.005, B2: 0.38, C2: 0.0092 },
    mu_a_base: 0.00018,
    mu_s_base: 0.0018,
    anisotropyG: 0.99,
  },
  {
    id: "acetone",
    name: "Acetone (C3H6O) [Solvent]",
    refractiveIndexStatic: 1.36,
    sellmeierCoeffs: { B1: 0.54, C1: 0.0049, B2: 0.37, C2: 0.0092 },
    mu_a_base: 0.00015,
    mu_s_base: 0.0015,
    anisotropyG: 0.99,
  },
  {
    id: "water_deionized",
    name: "Deionized Water (H2O) [Ultra-Pure]",
    refractiveIndexStatic: 1.333,
    sellmeierCoeffs: { B1: 0.567, C1: 0.0051, B2: 0.208, C2: 0.0131 },
    mu_a_base: 0.000005,
    mu_s_base: 0.0005,
    anisotropyG: 0.999,
  },
  {
    id: "water_distilled",
    name: "Distilled Water (H2O) [Laboratory]",
    refractiveIndexStatic: 1.333,
    sellmeierCoeffs: { B1: 0.567, C1: 0.0051, B2: 0.208, C2: 0.0131 },
    mu_a_base: 0.000008,
    mu_s_base: 0.0008,
    anisotropyG: 0.999,
  },
  {
    id: "water_heavy",
    name: "Heavy Water (D2O) [Reactor Grade]",
    refractiveIndexStatic: 1.328,
    sellmeierCoeffs: { B1: 0.559, C1: 0.0049, B2: 0.203, C2: 0.0131 },
    mu_a_base: 0.0001,
    mu_s_base: 0.008,
    anisotropyG: 0.999,
  },
  {
    id: "water_heavy_enriched",
    name: "Enriched Heavy Water (D2O 99.75%)",
    refractiveIndexStatic: 1.328,
    sellmeierCoeffs: { B1: 0.559, C1: 0.0049, B2: 0.203, C2: 0.0131 },
    mu_a_base: 0.00008,
    mu_s_base: 0.006,
    anisotropyG: 0.999,
  },
  {
    id: "saline",
    name: "Saline Solution (0.9% NaCl) [Medical]",
    refractiveIndexStatic: 1.34,
    sellmeierCoeffs: { B1: 0.51, C1: 0.0047, B2: 0.36, C2: 0.009 },
    mu_a_base: 0.0002,
    mu_s_base: 0.002,
    anisotropyG: 0.98,
  },
  {
    id: "ringers_lactate",
    name: "Ringer's Lactate [IV Fluid]",
    refractiveIndexStatic: 1.34,
    sellmeierCoeffs: { B1: 0.51, C1: 0.0047, B2: 0.36, C2: 0.009 },
    mu_a_base: 0.0002,
    mu_s_base: 0.002,
    anisotropyG: 0.98,
  },
  {
    id: "blood",
    name: "Whole Blood [Biological Fluid]",
    refractiveIndexStatic: 1.36,
    sellmeierCoeffs: { B1: 0.54, C1: 0.0049, B2: 0.37, C2: 0.0092 },
    mu_a_base: 2.5,
    mu_s_base: 120.0,
    anisotropyG: 0.85,
  },
  {
    id: "plasma",
    name: "Blood Plasma [Biological Fluid]",
    refractiveIndexStatic: 1.35,
    sellmeierCoeffs: { B1: 0.53, C1: 0.0048, B2: 0.36, C2: 0.0091 },
    mu_a_base: 0.15,
    mu_s_base: 8.0,
    anisotropyG: 0.92,
  },
  {
    id: "muscle",
    name: "Skeletal Muscle [Biological Tissue]",
    refractiveIndexStatic: 1.38,
    sellmeierCoeffs: { B1: 0.62, C1: 0.0058, B2: 0.25, C2: 0.0145 },
    mu_a_base: 0.12,
    mu_s_base: 18.0,
    anisotropyG: 0.89,
  },
  {
    id: "liver",
    name: "Liver Tissue [Biological Organ]",
    refractiveIndexStatic: 1.37,
    sellmeierCoeffs: { B1: 0.60, C1: 0.0056, B2: 0.24, C2: 0.0142 },
    mu_a_base: 0.18,
    mu_s_base: 22.0,
    anisotropyG: 0.88,
  },
  {
    id: "kidney",
    name: "Kidney Tissue [Biological Organ]",
    refractiveIndexStatic: 1.38,
    sellmeierCoeffs: { B1: 0.62, C1: 0.0058, B2: 0.25, C2: 0.0145 },
    mu_a_base: 0.15,
    mu_s_base: 20.0,
    anisotropyG: 0.89,
  },
  {
    id: "lung",
    name: "Lung Tissue [Biological Organ]",
    refractiveIndexStatic: 1.38,
    sellmeierCoeffs: { B1: 0.62, C1: 0.0058, B2: 0.25, C2: 0.0145 },
    mu_a_base: 0.10,
    mu_s_base: 12.0,
    anisotropyG: 0.91,
  },
  {
    id: "brain",
    name: "Brain Tissue [Biological Organ]",
    refractiveIndexStatic: 1.36,
    sellmeierCoeffs: { B1: 0.58, C1: 0.0054, B2: 0.23, C2: 0.0138 },
    mu_a_base: 0.14,
    mu_s_base: 16.0,
    anisotropyG: 0.90,
  },
  {
    id: "heart",
    name: "Heart Tissue [Biological Organ]",
    refractiveIndexStatic: 1.38,
    sellmeierCoeffs: { B1: 0.62, C1: 0.0058, B2: 0.25, C2: 0.0145 },
    mu_a_base: 0.13,
    mu_s_base: 17.0,
    anisotropyG: 0.89,
  },
  {
    id: "skin",
    name: "Skin Tissue [Biological Organ]",
    refractiveIndexStatic: 1.40,
    sellmeierCoeffs: { B1: 0.65, C1: 0.006, B2: 0.27, C2: 0.015 },
    mu_a_base: 0.25,
    mu_s_base: 30.0,
    anisotropyG: 0.87,
  },
  {
    id: "fat",
    name: "Adipose Tissue (Fat) [Biological]",
    refractiveIndexStatic: 1.44,
    sellmeierCoeffs: { B1: 0.68, C1: 0.0062, B2: 0.30, C2: 0.016 },
    mu_a_base: 0.08,
    mu_s_base: 10.0,
    anisotropyG: 0.92,
  },
  {
    id: "cartilage",
    name: "Cartilage Tissue [Biological]",
    refractiveIndexStatic: 1.40,
    sellmeierCoeffs: { B1: 0.65, C1: 0.006, B2: 0.27, C2: 0.015 },
    mu_a_base: 0.11,
    mu_s_base: 14.0,
    anisotropyG: 0.90,
  },
  {
    id: "tendon",
    name: "Tendon Tissue [Biological]",
    refractiveIndexStatic: 1.42,
    sellmeierCoeffs: { B1: 0.67, C1: 0.0061, B2: 0.28, C2: 0.0152 },
    mu_a_base: 0.09,
    mu_s_base: 13.0,
    anisotropyG: 0.91,
  },
  {
    id: "nerve",
    name: "Nerve Tissue [Biological]",
    refractiveIndexStatic: 1.37,
    sellmeierCoeffs: { B1: 0.60, C1: 0.0056, B2: 0.24, C2: 0.0142 },
    mu_a_base: 0.16,
    mu_s_base: 19.0,
    anisotropyG: 0.88,
  },
  {
    id: "eye_lens",
    name: "Eye Lens (Crystalline) [Ocular]",
    refractiveIndexStatic: 1.41,
    sellmeierCoeffs: { B1: 0.66, C1: 0.006, B2: 0.27, C2: 0.015 },
    mu_a_base: 0.02,
    mu_s_base: 3.0,
    anisotropyG: 0.93,
  },
  {
    id: "eye_vitreous",
    name: "Vitreous Humor [Ocular]",
    refractiveIndexStatic: 1.34,
    sellmeierCoeffs: { B1: 0.51, C1: 0.0047, B2: 0.36, C2: 0.009 },
    mu_a_base: 0.005,
    mu_s_base: 0.8,
    anisotropyG: 0.96,
  },
  {
    id: "eye_aqueous",
    name: "Aqueous Humor [Ocular]",
    refractiveIndexStatic: 1.34,
    sellmeierCoeffs: { B1: 0.51, C1: 0.0047, B2: 0.36, C2: 0.009 },
    mu_a_base: 0.004,
    mu_s_base: 0.7,
    anisotropyG: 0.96,
  },
  {
    id: "eye_cornea",
    name: "Cornea [Ocular]",
    refractiveIndexStatic: 1.38,
    sellmeierCoeffs: { B1: 0.62, C1: 0.0058, B2: 0.25, C2: 0.0145 },
    mu_a_base: 0.03,
    mu_s_base: 4.5,
    anisotropyG: 0.92,
  },
  {
    id: "retina",
    name: "Retina [Ocular]",
    refractiveIndexStatic: 1.36,
    sellmeierCoeffs: { B1: 0.58, C1: 0.0054, B2: 0.23, C2: 0.0138 },
    mu_a_base: 0.35,
    mu_s_base: 45.0,
    anisotropyG: 0.82,
  },
  {
    id: "membrane",
    name: "Cell Membrane [Biological]",
    refractiveIndexStatic: 1.46,
    sellmeierCoeffs: { B1: 0.70, C1: 0.0075, B2: 0.40, C2: 0.023 },
    mu_a_base: 0.5,
    mu_s_base: 60.0,
    anisotropyG: 0.80,
  },
  {
    id: "dna_solution",
    name: "DNA Solution [Biological]",
    refractiveIndexStatic: 1.35,
    sellmeierCoeffs: { B1: 0.53, C1: 0.0048, B2: 0.36, C2: 0.0091 },
    mu_a_base: 0.4,
    mu_s_base: 50.0,
    anisotropyG: 0.82,
  },
  {
    id: "protein_solution",
    name: "Protein Solution [Biological]",
    refractiveIndexStatic: 1.35,
    sellmeierCoeffs: { B1: 0.53, C1: 0.0048, B2: 0.36, C2: 0.0091 },
    mu_a_base: 0.3,
    mu_s_base: 40.0,
    anisotropyG: 0.83,
  },
  {
    id: "collagen",
    name: "Collagen [Biological]",
    refractiveIndexStatic: 1.47,
    sellmeierCoeffs: { B1: 0.70, C1: 0.0076, B2: 0.41, C2: 0.0232 },
    mu_a_base: 0.2,
    mu_s_base: 25.0,
    anisotropyG: 0.86,
  },
  {
    id: "hemoglobin",
    name: "Hemoglobin Solution [Biological]",
    refractiveIndexStatic: 1.36,
    sellmeierCoeffs: { B1: 0.54, C1: 0.0049, B2: 0.37, C2: 0.0092 },
    mu_a_base: 3.5,
    mu_s_base: 180.0,
    anisotropyG: 0.75,
  },
  {
    id: "melanin",
    name: "Melanin [Biological Pigment]",
    refractiveIndexStatic: 1.70,
    sellmeierCoeffs: { B1: 1.10, C1: 0.013, B2: 0.65, C2: 0.038 },
    mu_a_base: 45.0,
    mu_s_base: 850.0,
    anisotropyG: 0.60,
  },
  {
    id: "melanin_low",
    name: "Melanin (Low Concentration) [Biological]",
    refractiveIndexStatic: 1.55,
    sellmeierCoeffs: { B1: 0.82, C1: 0.0095, B2: 0.48, C2: 0.028 },
    mu_a_base: 8.0,
    mu_s_base: 180.0,
    anisotropyG: 0.78,
  },
  {
    id: "water_gel",
    name: "Water Gel Phantom [Medical Physics]",
    refractiveIndexStatic: 1.34,
    sellmeierCoeffs: { B1: 0.51, C1: 0.0047, B2: 0.36, C2: 0.009 },
    mu_a_base: 0.0001,
    mu_s_base: 0.001,
    anisotropyG: 0.999,
  },
  {
    id: "solid_water",
    name: "Solid Water Phantom [Dosimetry]",
    refractiveIndexStatic: 1.34,
    sellmeierCoeffs: { B1: 0.51, C1: 0.0047, B2: 0.36, C2: 0.009 },
    mu_a_base: 0.0001,
    mu_s_base: 0.001,
    anisotropyG: 0.999,
  },
  {
    id: "rw3",
    name: "RW3 Phantom [Dosimetry]",
    refractiveIndexStatic: 1.34,
    sellmeierCoeffs: { B1: 0.51, C1: 0.0047, B2: 0.36, C2: 0.009 },
    mu_a_base: 0.0001,
    mu_s_base: 0.001,
    anisotropyG: 0.999,
  },
  {
    id: "plastic_water",
    name: "Plastic Water [Dosimetry]",
    refractiveIndexStatic: 1.34,
    sellmeierCoeffs: { B1: 0.51, C1: 0.0047, B2: 0.36, C2: 0.009 },
    mu_a_base: 0.0001,
    mu_s_base: 0.001,
    anisotropyG: 0.999,
  },
  {
    id: "solid_water_elekta",
    name: "Elekta Solid Water [Dosimetry]",
    refractiveIndexStatic: 1.34,
    sellmeierCoeffs: { B1: 0.51, C1: 0.0047, B2: 0.36, C2: 0.009 },
    mu_a_base: 0.0001,
    mu_s_base: 0.001,
    anisotropyG: 0.999,
  },
  {
    id: "virtual_water",
    name: "Virtual Water [Dosimetry]",
    refractiveIndexStatic: 1.34,
    sellmeierCoeffs: { B1: 0.51, C1: 0.0047, B2: 0.36, C2: 0.009 },
    mu_a_base: 0.0001,
    mu_s_base: 0.001,
    anisotropyG: 0.999,
  },
  {
    id: "water_equivalent",
    name: "Water-Equivalent Plastic [Dosimetry]",
    refractiveIndexStatic: 1.34,
    sellmeierCoeffs: { B1: 0.51, C1: 0.0047, B2: 0.36, C2: 0.009 },
    mu_a_base: 0.0001,
    mu_s_base: 0.001,
    anisotropyG: 0.999,
  },
  {
    id: "cortical_bone",
    name: "Cortical Bone [Dosimetry]",
    refractiveIndexStatic: 1.55,
    sellmeierCoeffs: { B1: 0.78, C1: 0.0095, B2: 0.38, C2: 0.028 },
    mu_a_base: 0.15,
    mu_s_base: 25.0,
    anisotropyG: 0.88,
  },
  {
    id: "trabecular_bone",
    name: "Trabecular Bone [Dosimetry]",
    refractiveIndexStatic: 1.48,
    sellmeierCoeffs: { B1: 0.72, C1: 0.0082, B2: 0.41, C2: 0.024 },
    mu_a_base: 0.10,
    mu_s_base: 15.0,
    anisotropyG: 0.90,
  },
  {
    id: "lung_inhale",
    name: "Lung (Inhale) [Dosimetry]",
    refractiveIndexStatic: 1.38,
    sellmeierCoeffs: { B1: 0.62, C1: 0.0058, B2: 0.25, C2: 0.0145 },
    mu_a_base: 0.08,
    mu_s_base: 10.0,
    anisotropyG: 0.91,
  },
  {
    id: "lung_exhale",
    name: "Lung (Exhale) [Dosimetry]",
    refractiveIndexStatic: 1.38,
    sellmeierCoeffs: { B1: 0.62, C1: 0.0058, B2: 0.25, C2: 0.0145 },
    mu_a_base: 0.12,
    mu_s_base: 16.0,
    anisotropyG: 0.89,
  },
  {
    id: "adipose",
    name: "Adipose Tissue [Dosimetry]",
    refractiveIndexStatic: 1.44,
    sellmeierCoeffs: { B1: 0.68, C1: 0.0062, B2: 0.30, C2: 0.016 },
    mu_a_base: 0.08,
    mu_s_base: 10.0,
    anisotropyG: 0.92,
  },
  {
    id: "glandular",
    name: "Glandular Tissue [Dosimetry]",
    refractiveIndexStatic: 1.38,
    sellmeierCoeffs: { B1: 0.62, C1: 0.0058, B2: 0.25, C2: 0.0145 },
    mu_a_base: 0.14,
    mu_s_base: 17.0,
    anisotropyG: 0.89,
  },
  {
    id: "water_ice",
    name: "Water Ice (0°C) [Cryogenic]",
    refractiveIndexStatic: 1.31,
    sellmeierCoeffs: { B1: 0.48, C1: 0.0044, B2: 0.34, C2: 0.0085 },
    mu_a_base: 0.00005,
    mu_s_base: 0.0005,
    anisotropyG: 0.999,
  },
  {
    id: "water_ice_minus20",
    name: "Water Ice (-20°C) [Cryogenic]",
    refractiveIndexStatic: 1.30,
    sellmeierCoeffs: { B1: 0.47, C1: 0.0043, B2: 0.33, C2: 0.0083 },
    mu_a_base: 0.00004,
    mu_s_base: 0.0004,
    anisotropyG: 0.999,
  },
  {
    id: "water_ice_minus40",
    name: "Water Ice (-40°C) [Cryogenic]",
    refractiveIndexStatic: 1.29,
    sellmeierCoeffs: { B1: 0.46, C1: 0.0042, B2: 0.32, C2: 0.0081 },
    mu_a_base: 0.00003,
    mu_s_base: 0.0003,
    anisotropyG: 0.999,
  },
  {
    id: "dry_air",
    name: "Dry Air (0% RH) [Reference]",
    refractiveIndexStatic: 1.000293,
    sellmeierCoeffs: { B1: 0.000057, C1: 0.005, B2: 0.000032, C2: 0.014 },
    mu_a_base: 0.000001,
    mu_s_base: 0.00001,
    anisotropyG: 0.999,
  },
  {
    id: "humid_air",
    name: "Humid Air (50% RH) [Reference]",
    refractiveIndexStatic: 1.00032,
    sellmeierCoeffs: { B1: 0.000062, C1: 0.0052, B2: 0.000035, C2: 0.0145 },
    mu_a_base: 0.0000012,
    mu_s_base: 0.000012,
    anisotropyG: 0.999,
  },
  {
    id: "co2",
    name: "Carbon Dioxide (CO2) [Gas]",
    refractiveIndexStatic: 1.00045,
    sellmeierCoeffs: { B1: 0.000072, C1: 0.0055, B2: 0.000041, C2: 0.015 },
    mu_a_base: 0.0000015,
    mu_s_base: 0.000015,
    anisotropyG: 0.999,
  },
  {
    id: "n2",
    name: "Nitrogen Gas (N2) [Purge Gas]",
    refractiveIndexStatic: 1.0003,
    sellmeierCoeffs: { B1: 0.000058, C1: 0.0051, B2: 0.000033, C2: 0.0142 },
    mu_a_base: 0.000001,
    mu_s_base: 0.00001,
    anisotropyG: 0.999,
  },
  {
    id: "argon",
    name: "Argon Gas (Ar) [Purge Gas]",
    refractiveIndexStatic: 1.00028,
    sellmeierCoeffs: { B1: 0.000056, C1: 0.005, B2: 0.000031, C2: 0.014 },
    mu_a_base: 0.0000009,
    mu_s_base: 0.000009,
    anisotropyG: 0.999,
  },
  {
    id: "helium",
    name: "Helium Gas (He) [Purge Gas]",
    refractiveIndexStatic: 1.000035,
    sellmeierCoeffs: { B1: 0.000007, C1: 0.0048, B2: 0.000004, C2: 0.0135 },
    mu_a_base: 0.0000003,
    mu_s_base: 0.000003,
    anisotropyG: 0.999,
  },
  {
    id: "hydrogen",
    name: "Hydrogen Gas (H2) [Purge Gas]",
    refractiveIndexStatic: 1.000132,
    sellmeierCoeffs: { B1: 0.000027, C1: 0.0049, B2: 0.000015, C2: 0.0138 },
    mu_a_base: 0.0000005,
    mu_s_base: 0.000005,
    anisotropyG: 0.999,
  },
  {
    id: "oxygen",
    name: "Oxygen Gas (O2) [Purge Gas]",
    refractiveIndexStatic: 1.00027,
    sellmeierCoeffs: { B1: 0.000055, C1: 0.005, B2: 0.000031, C2: 0.014 },
    mu_a_base: 0.0000009,
    mu_s_base: 0.000009,
    anisotropyG: 0.999,
  },
  {
    id: "xenon",
    name: "Xenon Gas (Xe) [Propulsion]",
    refractiveIndexStatic: 1.00172,
    sellmeierCoeffs: { B1: 0.00035, C1: 0.006, B2: 0.00020, C2: 0.016 },
    mu_a_base: 0.000005,
    mu_s_base: 0.00005,
    anisotropyG: 0.999,
  },
  {
    id: "krypton",
    name: "Krypton Gas (Kr) [Insulation]",
    refractiveIndexStatic: 1.00149,
    sellmeierCoeffs: { B1: 0.00030, C1: 0.0058, B2: 0.00017, C2: 0.0155 },
    mu_a_base: 0.000004,
    mu_s_base: 0.00004,
    anisotropyG: 0.999,
  },
  {
    id: "neon",
    name: "Neon Gas (Ne) [Lighting]",
    refractiveIndexStatic: 1.00006,
    sellmeierCoeffs: { B1: 0.000012, C1: 0.0047, B2: 0.000007, C2: 0.0132 },
    mu_a_base: 0.0000002,
    mu_s_base: 0.000002,
    anisotropyG: 0.999,
  },
  {
    id: "argon_ionized",
    name: "Ionized Argon Plasma [Processing]",
    refractiveIndexStatic: 1.00028,
    sellmeierCoeffs: { B1: 0.000056, C1: 0.005, B2: 0.000031, C2: 0.014 },
    mu_a_base: 0.000002,
    mu_s_base: 0.00002,
    anisotropyG: 0.999,
  },
  {
    id: "sf6",
    name: "SF6 Gas (Sulfur Hexafluoride) [Insulation]",
    refractiveIndexStatic: 1.00073,
    sellmeierCoeffs: { B1: 0.00015, C1: 0.0055, B2: 0.000085, C2: 0.015 },
    mu_a_base: 0.0000025,
    mu_s_base: 0.000025,
    anisotropyG: 0.999,
  },
  {
    id: "c4f8",
    name: "C4F8 Gas (Perfluorocyclobutane) [Etching]",
    refractiveIndexStatic: 1.00035,
    sellmeierCoeffs: { B1: 0.00007, C1: 0.0052, B2: 0.00004, C2: 0.0143 },
    mu_a_base: 0.0000012,
    mu_s_base: 0.000012,
    anisotropyG: 0.999,
  },
  {
    id: "chf3",
    name: "CHF3 Gas (Trifluoromethane) [Etching]",
    refractiveIndexStatic: 1.00022,
    sellmeierCoeffs: { B1: 0.000045, C1: 0.005, B2: 0.000025, C2: 0.0138 },
    mu_a_base: 0.0000008,
    mu_s_base: 0.000008,
    anisotropyG: 0.999,
  },
  {
    id: "bcl3",
    name: "BCl3 Gas (Boron Trichloride) [Doping]",
    refractiveIndexStatic: 1.00028,
    sellmeierCoeffs: { B1: 0.000056, C1: 0.005, B2: 0.000031, C2: 0.014 },
    mu_a_base: 0.0000009,
    mu_s_base: 0.000009,
    anisotropyG: 0.999,
  },
  {
    id: "cl2",
    name: "Cl2 Gas (Chlorine) [Etching]",
    refractiveIndexStatic: 1.00045,
    sellmeierCoeffs: { B1: 0.000072, C1: 0.0055, B2: 0.000041, C2: 0.015 },
    mu_a_base: 0.0000015,
    mu_s_base: 0.000015,
    anisotropyG: 0.999,
  },
  {
    id: "hbr",
    name: "HBr Gas (Hydrogen Bromide) [Etching]",
    refractiveIndexStatic: 1.00032,
    sellmeierCoeffs: { B1: 0.000065, C1: 0.0053, B2: 0.000037, C2: 0.0146 },
    mu_a_base: 0.0000011,
    mu_s_base: 0.000011,
    anisotropyG: 0.999,
  },
  {
    id: "hcl",
    name: "HCl Gas (Hydrogen Chloride) [Etching]",
    refractiveIndexStatic: 1.00022,
    sellmeierCoeffs: { B1: 0.000045, C1: 0.005, B2: 0.000025, C2: 0.0138 },
    mu_a_base: 0.0000008,
    mu_s_base: 0.000008,
    anisotropyG: 0.999,
  },
  {
    id: "ph3",
    name: "PH3 Gas (Phosphine) [Doping]",
    refractiveIndexStatic: 1.00026,
    sellmeierCoeffs: { B1: 0.000052, C1: 0.0051, B2: 0.000029, C2: 0.014 },
    mu_a_base: 0.0000009,
    mu_s_base: 0.000009,
    anisotropyG: 0.999,
  },
  {
    id: "ash3",
    name: "AsH3 Gas (Arsine) [Doping]",
    refractiveIndexStatic: 1.00038,
    sellmeierCoeffs: { B1: 0.000075, C1: 0.0054, B2: 0.000042, C2: 0.0148 },
    mu_a_base: 0.0000013,
    mu_s_base: 0.000013,
    anisotropyG: 0.999,
  },
  {
    id: "sih4",
    name: "SiH4 Gas (Silane) [Deposition]",
    refractiveIndexStatic: 1.00027,
    sellmeierCoeffs: { B1: 0.000054, C1: 0.0051, B2: 0.000030, C2: 0.014 },
    mu_a_base: 0.0000009,
    mu_s_base: 0.000009,
    anisotropyG: 0.999,
  },
  {
    id: "geh4",
    name: "GeH4 Gas (Germane) [Deposition]",
    refractiveIndexStatic: 1.00042,
    sellmeierCoeffs: { B1: 0.00007, C1: 0.0054, B2: 0.000039, C2: 0.0147 },
    mu_a_base: 0.0000014,
    mu_s_base: 0.000014,
    anisotropyG: 0.999,
  },
  {
    id: "b2h6",
    name: "B2H6 Gas (Diborane) [Doping]",
    refractiveIndexStatic: 1.00035,
    sellmeierCoeffs: { B1: 0.000068, C1: 0.0052, B2: 0.000038, C2: 0.0144 },
    mu_a_base: 0.0000012,
    mu_s_base: 0.000012,
    anisotropyG: 0.999,
  },
  {
    id: "wf6",
    name: "WF6 Gas (Tungsten Hexafluoride) [Deposition]",
    refractiveIndexStatic: 1.00085,
    sellmeierCoeffs: { B1: 0.00018, C1: 0.006, B2: 0.00010, C2: 0.016 },
    mu_a_base: 0.000003,
    mu_s_base: 0.00003,
    anisotropyG: 0.999,
  },
  {
    id: "moF6",
    name: "MoF6 Gas (Molybdenum Hexafluoride) [Deposition]",
    refractiveIndexStatic: 1.00078,
    sellmeierCoeffs: { B1: 0.00016, C1: 0.0058, B2: 0.00009, C2: 0.0158 },
    mu_a_base: 0.0000028,
    mu_s_base: 0.000028,
    anisotropyG: 0.999,
  },
  {
    id: "nbF5",
    name: "NbF5 Gas (Niobium Pentafluoride) [Deposition]",
    refractiveIndexStatic: 1.00072,
    sellmeierCoeffs: { B1: 0.00015, C1: 0.0057, B2: 0.000085, C2: 0.0155 },
    mu_a_base: 0.0000025,
    mu_s_base: 0.000025,
    anisotropyG: 0.999,
  },
  {
    id: "taf5",
    name: "TaF5 Gas (Tantalum Pentafluoride) [Deposition]",
    refractiveIndexStatic: 1.00082,
    sellmeierCoeffs: { B1: 0.00017, C1: 0.0059, B2: 0.000095, C2: 0.0159 },
    mu_a_base: 0.0000029,
    mu_s_base: 0.000029,
    anisotropyG: 0.999,
  },
  {
    id: "tif4",
    name: "TiF4 Gas (Titanium Tetrafluoride) [Deposition]",
    refractiveIndexStatic: 1.00068,
    sellmeierCoeffs: { B1: 0.00014, C1: 0.0056, B2: 0.00008, C2: 0.0152 },
    mu_a_base: 0.0000023,
    mu_s_base: 0.000023,
    anisotropyG: 0.999,
  },
  {
    id: "zrf4",
    name: "ZrF4 Gas (Zirconium Tetrafluoride) [Deposition]",
    refractiveIndexStatic: 1.00075,
    sellmeierCoeffs: { B1: 0.00016, C1: 0.0058, B2: 0.00009, C2: 0.0157 },
    mu_a_base: 0.0000026,
    mu_s_base: 0.000026,
    anisotropyG: 0.999,
  },
  {
    id: "hafnium",
    name: "Hafnium (Hf) [Refractory Metal]",
    refractiveIndexStatic: 2.45,
    sellmeierCoeffs: { B1: 2.20, C1: 0.0215, B2: 1.38, C2: 0.070 },
    mu_a_base: 0.008,
    mu_s_base: 0.045,
    anisotropyG: 0.86,
  },
  {
    id: "zirconium",
    name: "Zirconium (Zr) [Refractory Metal]",
    refractiveIndexStatic: 2.35,
    sellmeierCoeffs: { B1: 2.08, C1: 0.0205, B2: 1.30, C2: 0.067 },
    mu_a_base: 0.007,
    mu_s_base: 0.042,
    anisotropyG: 0.87,
  },
  {
    id: "titanium",
    name: "Titanium (Ti) [Refractory Metal]",
    refractiveIndexStatic: 2.16,
    sellmeierCoeffs: { B1: 1.90, C1: 0.019, B2: 1.18, C2: 0.062 },
    mu_a_base: 0.005,
    mu_s_base: 0.035,
    anisotropyG: 0.88,
  },
  {
    id: "tungsten",
    name: "Tungsten (W) [Refractory Metal]",
    refractiveIndexStatic: 3.50,
    sellmeierCoeffs: { B1: 3.15, C1: 0.028, B2: 1.98, C2: 0.085 },
    mu_a_base: 0.012,
    mu_s_base: 0.055,
    anisotropyG: 0.84,
  },
  {
    id: "molybdenum",
    name: "Molybdenum (Mo) [Refractory Metal]",
    refractiveIndexStatic: 3.25,
    sellmeierCoeffs: { B1: 2.90, C1: 0.026, B2: 1.82, C2: 0.080 },
    mu_a_base: 0.010,
    mu_s_base: 0.050,
    anisotropyG: 0.85,
  },
  {
    id: "niobium",
    name: "Niobium (Nb) [Superconductor]",
    refractiveIndexStatic: 2.85,
    sellmeierCoeffs: { B1: 2.52, C1: 0.0245, B2: 1.58, C2: 0.075 },
    mu_a_base: 0.008,
    mu_s_base: 0.045,
    anisotropyG: 0.86,
  },
  {
    id: "tantalum",
    name: "Tantalum (Ta) [Refractory Metal]",
    refractiveIndexStatic: 2.95,
    sellmeierCoeffs: { B1: 2.62, C1: 0.0255, B2: 1.65, C2: 0.078 },
    mu_a_base: 0.009,
    mu_s_base: 0.048,
    anisotropyG: 0.85,
  },
  {
    id: "vanadium",
    name: "Vanadium (V) [Refractory Metal]",
    refractiveIndexStatic: 2.65,
    sellmeierCoeffs: { B1: 2.32, C1: 0.023, B2: 1.45, C2: 0.072 },
    mu_a_base: 0.007,
    mu_s_base: 0.042,
    anisotropyG: 0.87,
  },
  {
    id: "chromium",
    name: "Chromium (Cr) [Refractory Metal]",
    refractiveIndexStatic: 3.15,
    sellmeierCoeffs: { B1: 2.78, C1: 0.026, B2: 1.75, C2: 0.080 },
    mu_a_base: 0.009,
    mu_s_base: 0.048,
    anisotropyG: 0.86,
  },
  {
    id: "iron",
    name: "Iron (Fe) [Ferromagnetic]",
    refractiveIndexStatic: 2.95,
    sellmeierCoeffs: { B1: 2.60, C1: 0.025, B2: 1.62, C2: 0.077 },
    mu_a_base: 0.008,
    mu_s_base: 0.045,
    anisotropyG: 0.86,
  },
  {
    id: "nickel",
    name: "Nickel (Ni) [Ferromagnetic]",
    refractiveIndexStatic: 2.25,
    sellmeierCoeffs: { B1: 1.98, C1: 0.020, B2: 1.24, C2: 0.065 },
    mu_a_base: 0.006,
    mu_s_base: 0.038,
    anisotropyG: 0.88,
  },
  {
    id: "cobalt",
    name: "Cobalt (Co) [Ferromagnetic]",
    refractiveIndexStatic: 2.05,
    sellmeierCoeffs: { B1: 1.78, C1: 0.0185, B2: 1.12, C2: 0.060 },
    mu_a_base: 0.005,
    mu_s_base: 0.035,
    anisotropyG: 0.89,
  },
  {
    id: "copper",
    name: "Copper (Cu) [Conductor]",
    refractiveIndexStatic: 0.22,
    sellmeierCoeffs: { B1: 0.12, C1: 0.002, B2: 0.08, C2: 0.004 },
    mu_a_base: 0.0001,
    mu_s_base: 0.001,
    anisotropyG: 0.99,
  },
  {
    id: "gold",
    name: "Gold (Au) [Noble Metal]",
    refractiveIndexStatic: 0.47,
    sellmeierCoeffs: { B1: 0.25, C1: 0.003, B2: 0.16, C2: 0.006 },
    mu_a_base: 0.0002,
    mu_s_base: 0.002,
    anisotropyG: 0.99,
  },
  {
    id: "silver",
    name: "Silver (Ag) [Noble Metal]",
    refractiveIndexStatic: 0.15,
    sellmeierCoeffs: { B1: 0.08, C1: 0.0015, B2: 0.05, C2: 0.003 },
    mu_a_base: 0.00005,
    mu_s_base: 0.0005,
    anisotropyG: 0.999,
  },
  {
    id: "aluminum",
    name: "Aluminum (Al) [Conductor]",
    refractiveIndexStatic: 1.44,
    sellmeierCoeffs: { B1: 0.68, C1: 0.0075, B2: 0.40, C2: 0.023 },
    mu_a_base: 0.0003,
    mu_s_base: 0.003,
    anisotropyG: 0.98,
  },
  {
    id: "platinum",
    name: "Platinum (Pt) [Noble Metal]",
    refractiveIndexStatic: 2.35,
    sellmeierCoeffs: { B1: 2.08, C1: 0.0205, B2: 1.30, C2: 0.067 },
    mu_a_base: 0.007,
    mu_s_base: 0.042,
    anisotropyG: 0.87,
  },
  {
    id: "palladium",
    name: "Palladium (Pd) [Noble Metal]",
    refractiveIndexStatic: 1.85,
    sellmeierCoeffs: { B1: 1.35, C1: 0.014, B2: 0.88, C2: 0.046 },
    mu_a_base: 0.004,
    mu_s_base: 0.030,
    anisotropyG: 0.90,
  },
  {
    id: "rhodium",
    name: "Rhodium (Rh) [Noble Metal]",
    refractiveIndexStatic: 2.05,
    sellmeierCoeffs: { B1: 1.78, C1: 0.0185, B2: 1.12, C2: 0.060 },
    mu_a_base: 0.005,
    mu_s_base: 0.035,
    anisotropyG: 0.89,
  },
  {
    id: "iridium",
    name: "Iridium (Ir) [Noble Metal]",
    refractiveIndexStatic: 2.45,
    sellmeierCoeffs: { B1: 2.20, C1: 0.0215, B2: 1.38, C2: 0.070 },
    mu_a_base: 0.008,
    mu_s_base: 0.045,
    anisotropyG: 0.86,
  },
  {
    id: "osmium",
    name: "Osmium (Os) [Densest Metal]",
    refractiveIndexStatic: 3.15,
    sellmeierCoeffs: { B1: 2.78, C1: 0.026, B2: 1.75, C2: 0.080 },
    mu_a_base: 0.009,
    mu_s_base: 0.048,
    anisotropyG: 0.86,
  },
  {
    id: "rhenium",
    name: "Rhenium (Re) [Refractory Metal]",
    refractiveIndexStatic: 3.05,
    sellmeierCoeffs: { B1: 2.70, C1: 0.0255, B2: 1.70, C2: 0.078 },
    mu_a_base: 0.0085,
    mu_s_base: 0.046,
    anisotropyG: 0.86,
  },
  {
    id: "ruthenium",
    name: "Ruthenium (Ru) [Noble Metal]",
    refractiveIndexStatic: 2.85,
    sellmeierCoeffs: { B1: 2.52, C1: 0.0245, B2: 1.58, C2: 0.075 },
    mu_a_base: 0.008,
    mu_s_base: 0.045,
    anisotropyG: 0.86,
  },
  {
    id: "osmium_alloy",
    name: "Osmium Alloy [High-Density]",
    refractiveIndexStatic: 3.20,
    sellmeierCoeffs: { B1: 2.85, C1: 0.0265, B2: 1.78, C2: 0.081 },
    mu_a_base: 0.0095,
    mu_s_base: 0.050,
    anisotropyG: 0.85,
  },
  {
    id: "depleted_uranium",
    name: "Depleted Uranium (DU) [High-Density]",
    refractiveIndexStatic: 2.50,
    sellmeierCoeffs: { B1: 2.25, C1: 0.022, B2: 1.42, C2: 0.072 },
    mu_a_base: 0.0085,
    mu_s_base: 0.046,
    anisotropyG: 0.86,
  },
  {
    id: "lead",
    name: "Lead (Pb) [Radiation Shield]",
    refractiveIndexStatic: 2.52,
    sellmeierCoeffs: { B1: 2.28, C1: 0.0225, B2: 1.44, C2: 0.073 },
    mu_a_base: 0.009,
    mu_s_base: 0.048,
    anisotropyG: 0.85,
  },
  {
    id: "tungsten_alloy",
    name: "Tungsten Alloy (W-Ni-Fe) [Shielding]",
    refractiveIndexStatic: 3.10,
    sellmeierCoeffs: { B1: 2.75, C1: 0.026, B2: 1.72, C2: 0.079 },
    mu_a_base: 0.0088,
    mu_s_base: 0.047,
    anisotropyG: 0.86,
  },
  {
    id: "steel",
    name: "Stainless Steel 304 [Structural]",
    refractiveIndexStatic: 2.85,
    sellmeierCoeffs: { B1: 2.52, C1: 0.0245, B2: 1.58, C2: 0.075 },
    mu_a_base: 0.008,
    mu_s_base: 0.045,
    anisotropyG: 0.86,
  },
  {
    id: "steel_316",
    name: "Stainless Steel 316 [Marine Grade]",
    refractiveIndexStatic: 2.88,
    sellmeierCoeffs: { B1: 2.55, C1: 0.0248, B2: 1.60, C2: 0.076 },
    mu_a_base: 0.0082,
    mu_s_base: 0.046,
    anisotropyG: 0.86,
  },
  {
    id: "inconel",
    name: "Inconel 718 [Superalloy]",
    refractiveIndexStatic: 2.95,
    sellmeierCoeffs: { B1: 2.62, C1: 0.0255, B2: 1.65, C2: 0.078 },
    mu_a_base: 0.0085,
    mu_s_base: 0.047,
    anisotropyG: 0.86,
  },
  {
    id: "titanium_alloy",
    name: "Titanium Alloy (Ti-6Al-4V) [Aerospace]",
    refractiveIndexStatic: 2.18,
    sellmeierCoeffs: { B1: 1.92, C1: 0.019, B2: 1.20, C2: 0.062 },
    mu_a_base: 0.0055,
    mu_s_base: 0.036,
    anisotropyG: 0.88,
  },
  {
    id: "aluminum_alloy",
    name: "Aluminum Alloy 6061 [Structural]",
    refractiveIndexStatic: 1.44,
    sellmeierCoeffs: { B1: 0.68, C1: 0.0075, B2: 0.40, C2: 0.023 },
    mu_a_base: 0.0003,
    mu_s_base: 0.003,
    anisotropyG: 0.98,
  },
  {
    id: "copper_alloy",
    name: "Copper Alloy (Cu-Be) [Spring]",
    refractiveIndexStatic: 0.22,
    sellmeierCoeffs: { B1: 0.12, C1: 0.002, B2: 0.08, C2: 0.004 },
    mu_a_base: 0.0001,
    mu_s_base: 0.001,
    anisotropyG: 0.99,
  },
  {
    id: "brass",
    name: "Brass (Cu-Zn) [Decorative]",
    refractiveIndexStatic: 0.38,
    sellmeierCoeffs: { B1: 0.20, C1: 0.0025, B2: 0.13, C2: 0.005 },
    mu_a_base: 0.00015,
    mu_s_base: 0.0015,
    anisotropyG: 0.99,
  },
  {
    id: "bronze",
    name: "Bronze (Cu-Sn) [Structural]",
    refractiveIndexStatic: 0.42,
    sellmeierCoeffs: { B1: 0.22, C1: 0.0028, B2: 0.14, C2: 0.0052 },
    mu_a_base: 0.00018,
    mu_s_base: 0.0018,
    anisotropyG: 0.99,
  },
  {
    id: "graphite",
    name: "Graphite (C) [Lubricant]",
    refractiveIndexStatic: 2.65,
    sellmeierCoeffs: { B1: 2.35, C1: 0.023, B2: 1.48, C2: 0.076 },
    mu_a_base: 0.007,
    mu_s_base: 0.042,
    anisotropyG: 0.87,
  },
  {
    id: "graphene",
    name: "Graphene [2D Material]",
    refractiveIndexStatic: 2.6,
    sellmeierCoeffs: { B1: 2.30, C1: 0.0225, B2: 1.44, C2: 0.074 },
    mu_a_base: 0.0065,
    mu_s_base: 0.040,
    anisotropyG: 0.87,
  },
  {
    id: "carbon_nanotube",
    name: "Carbon Nanotube [Nanomaterial]",
    refractiveIndexStatic: 2.55,
    sellmeierCoeffs: { B1: 2.25, C1: 0.022, B2: 1.41, C2: 0.073 },
    mu_a_base: 0.006,
    mu_s_base: 0.038,
    anisotropyG: 0.88,
  },
  {
    id: "boron_nitride",
    name: "Boron Nitride (BN) [Ceramic]",
    refractiveIndexStatic: 2.15,
    sellmeierCoeffs: { B1: 1.88, C1: 0.019, B2: 1.18, C2: 0.065 },
    mu_a_base: 0.005,
    mu_s_base: 0.035,
    anisotropyG: 0.89,
  },
  {
    id: "boron_nitride_hbn",
    name: "Hexagonal BN (h-BN) [2D Material]",
    refractiveIndexStatic: 2.10,
    sellmeierCoeffs: { B1: 1.82, C1: 0.0185, B2: 1.14, C2: 0.063 },
    mu_a_base: 0.0048,
    mu_s_base: 0.034,
    anisotropyG: 0.89,
  },
  {
    id: "molybdenum_disulfide",
    name: "MoS2 (Molybdenum Disulfide) [2D Material]",
    refractiveIndexStatic: 4.55,
    sellmeierCoeffs: { B1: 4.10, C1: 0.032, B2: 2.58, C2: 0.095 },
    mu_a_base: 0.035,
    mu_s_base: 0.065,
    anisotropyG: 0.78,
  },
  {
    id: "tungsten_disulfide",
    name: "WS2 (Tungsten Disulfide) [Lubricant]",
    refractiveIndexStatic: 4.35,
    sellmeierCoeffs: { B1: 3.90, C1: 0.031, B2: 2.46, C2: 0.092 },
    mu_a_base: 0.032,
    mu_s_base: 0.062,
    anisotropyG: 0.79,
  },
  {
    id: "black_phosphorus",
    name: "Black Phosphorus [2D Material]",
    refractiveIndexStatic: 3.85,
    sellmeierCoeffs: { B1: 3.45, C1: 0.030, B2: 2.17, C2: 0.088 },
    mu_a_base: 0.028,
    mu_s_base: 0.058,
    anisotropyG: 0.80,
  },
  {
    id: "transition_metal_dichalcogenide",
    name: "TMD (Generic) [2D Material]",
    refractiveIndexStatic: 4.20,
    sellmeierCoeffs: { B1: 3.75, C1: 0.031, B2: 2.36, C2: 0.090 },
    mu_a_base: 0.030,
    mu_s_base: 0.060,
    anisotropyG: 0.79,
  },
  {
    id: "perovskite",
    name: "Perovskite (CH3NH3PbI3) [Solar Cell]",
    refractiveIndexStatic: 2.45,
    sellmeierCoeffs: { B1: 2.18, C1: 0.0215, B2: 1.37, C2: 0.070 },
    mu_a_base: 0.008,
    mu_s_base: 0.045,
    anisotropyG: 0.86,
  },
  {
    id: "perovskite_solar",
    name: "Perovskite Solar Cell [Photovoltaic]",
    refractiveIndexStatic: 2.45,
    sellmeierCoeffs: { B1: 2.18, C1: 0.0215, B2: 1.37, C2: 0.070 },
    mu_a_base: 0.008,
    mu_s_base: 0.045,
    anisotropyG: 0.86,
  },
  {
    id: "quantum_dot",
    name: "Quantum Dot Film [Nanophotonic]",
    refractiveIndexStatic: 1.65,
    sellmeierCoeffs: { B1: 0.92, C1: 0.0105, B2: 0.55, C2: 0.032 },
    mu_a_base: 0.002,
    mu_s_base: 0.015,
    anisotropyG: 0.94,
  },
  {
    id: "metamaterial",
    name: "Metamaterial [Artificial]",
    refractiveIndexStatic: 1.50,
    sellmeierCoeffs: { B1: 0.75, C1: 0.0088, B2: 0.44, C2: 0.026 },
    mu_a_base: 0.001,
    mu_s_base: 0.01,
    anisotropyG: 0.95,
  },
  {
    id: "metamaterial_negative",
    name: "Negative Index Metamaterial [Exotic]",
    refractiveIndexStatic: -1.5,
    sellmeierCoeffs: { B1: -0.75, C1: 0.0088, B2: -0.44, C2: 0.026 },
    mu_a_base: 0.001,
    mu_s_base: 0.01,
    anisotropyG: 0.95,
  },
  {
    id: "photonic_crystal",
    name: "Photonic Crystal [Bandgap Material]",
    refractiveIndexStatic: 1.80,
    sellmeierCoeffs: { B1: 1.22, C1: 0.012, B2: 0.80, C2: 0.044 },
    mu_a_base: 0.001,
    mu_s_base: 0.011,
    anisotropyG: 0.95,
  },
  {
    id: "plasmonic",
    name: "Plasmonic Material [Nanophotonic]",
    refractiveIndexStatic: 2.50,
    sellmeierCoeffs: { B1: 2.25, C1: 0.022, B2: 1.42, C2: 0.072 },
    mu_a_base: 0.008,
    mu_s_base: 0.046,
    anisotropyG: 0.86,
  },
  {
    id: "superconducting",
    name: "Superconducting Film [Cryogenic]",
    refractiveIndexStatic: 2.80,
    sellmeierCoeffs: { B1: 2.48, C1: 0.024, B2: 1.55, C2: 0.074 },
    mu_a_base: 0.0001,
    mu_s_base: 0.001,
    anisotropyG: 0.99,
  },
  {
    id: "superconducting_niobium",
    name: "Niobium Superconductor [RF Cavity]",
    refractiveIndexStatic: 2.85,
    sellmeierCoeffs: { B1: 2.52, C1: 0.0245, B2: 1.58, C2: 0.075 },
    mu_a_base: 0.0001,
    mu_s_base: 0.001,
    anisotropyG: 0.99,
  },
  {
    id: "superconducting_nbti",
    name: "NbTi Superconductor [Wire]",
    refractiveIndexStatic: 2.85,
    sellmeierCoeffs: { B1: 2.52, C1: 0.0245, B2: 1.58, C2: 0.075 },
    mu_a_base: 0.0001,
    mu_s_base: 0.001,
    anisotropyG: 0.99,
  },
  {
    id: "superconducting_nb3sn",
    name: "Nb3Sn Superconductor [Wire]",
    refractiveIndexStatic: 2.90,
    sellmeierCoeffs: { B1: 2.58, C1: 0.025, B2: 1.62, C2: 0.076 },
    mu_a_base: 0.0001,
    mu_s_base: 0.001,
    anisotropyG: 0.99,
  },
  {
    id: "superconducting_mgB2",
    name: "MgB2 Superconductor [Wire]",
    refractiveIndexStatic: 2.75,
    sellmeierCoeffs: { B1: 2.42, C1: 0.0235, B2: 1.52, C2: 0.073 },
    mu_a_base: 0.0001,
    mu_s_base: 0.001,
    anisotropyG: 0.99,
  },
  {
    id: "superconducting_hbc",
    name: "HBC Superconductor [High-Tc]",
    refractiveIndexStatic: 2.95,
    sellmeierCoeffs: { B1: 2.62, C1: 0.0255, B2: 1.65, C2: 0.078 },
    mu_a_base: 0.0001,
    mu_s_base: 0.001,
    anisotropyG: 0.99,
  },
  {
    id: "superconducting_ybco",
    name: "YBCO Superconductor [High-Tc]",
    refractiveIndexStatic: 3.00,
    sellmeierCoeffs: { B1: 2.68, C1: 0.026, B2: 1.68, C2: 0.079 },
    mu_a_base: 0.0001,
    mu_s_base: 0.001,
    anisotropyG: 0.99,
  },
  {
    id: "superconducting_bscco",
    name: "BSCCO Superconductor [High-Tc]",
    refractiveIndexStatic: 2.98,
    sellmeierCoeffs: { B1: 2.65, C1: 0.0258, B2: 1.67, C2: 0.0785 },
    mu_a_base: 0.0001,
    mu_s_base: 0.001,
    anisotropyG: 0.99,
  },
  {
    id: "superconducting_fe",
    name: "Iron-Based Superconductor [High-Tc]",
    refractiveIndexStatic: 2.92,
    sellmeierCoeffs: { B1: 2.60, C1: 0.0252, B2: 1.63, C2: 0.0775 },
    mu_a_base: 0.0001,
    mu_s_base: 0.001,
    anisotropyG: 0.99,
  },
  {
    id: "liquid_xenon",
    name: "Liquid Xenon (LXe) [Dark Matter / Neutrino Detector]",
    refractiveIndexStatic: 1.69,
    sellmeierCoeffs: { B1: 1.45, C1: 0.008, B2: 0.95, C2: 0.035 },
    mu_a_base: 0.00005,
    mu_s_base: 0.0002,
    anisotropyG: 0.999,
  },
  {
    id: "lead_tungstate",
    name: "Lead Tungstate (PbWO4) [CMS ECAL Crystal]",
    refractiveIndexStatic: 2.16,
    sellmeierCoeffs: { B1: 2.10, C1: 0.015, B2: 1.25, C2: 0.050 },
    mu_a_base: 0.01,
    mu_s_base: 0.05,
    anisotropyG: 0.90,
  },
  {
    id: "scintillating_glass",
    name: "Cerium-doped Scintillating Glass (GS-20) [Neutron Detector]",
    refractiveIndexStatic: 1.55,
    sellmeierCoeffs: { B1: 0.85, C1: 0.015, B2: 0.25, C2: 0.045 },
    mu_a_base: 0.005,
    mu_s_base: 0.02,
    anisotropyG: 0.9,
  },
  {
    id: "liquid_xenon",
    name: "Liquid Xenon (LXe) [Dark Matter / Neutrinoless Double Beta]",
    refractiveIndexStatic: 1.36,
    sellmeierCoeffs: { B1: 0.5, C1: 0.01, B2: 0.3, C2: 0.02 },
    mu_a_base: 0.0001,
    mu_s_base: 0.002,
    anisotropyG: 0.999,
  },
  {
    id: "sapphire",
    name: "Synthetic Sapphire (Al2O3) [Cryogenic Bolometer]",
    refractiveIndexStatic: 1.76,
    sellmeierCoeffs: { B1: 1.02, C1: 0.003, B2: 1.05, C2: 0.013 },
    mu_a_base: 0.001,
    mu_s_base: 0.01,
    anisotropyG: 0.95,
  },
  {
    id: "bgo",
    name: "Bismuth Germanate (BGO) [Dense EM Calorimeter]",
    refractiveIndexStatic: 2.15,
    sellmeierCoeffs: { B1: 1.5, C1: 0.05, B2: 0.8, C2: 0.1 },
    mu_a_base: 0.02,
    mu_s_base: 0.05,
    anisotropyG: 0.85,
  },
  {
    id: "teflon",
    name: "PTFE / Teflon [High Reflectance Cavity Liner]",
    refractiveIndexStatic: 1.35,
    sellmeierCoeffs: { B1: 0.45, C1: 0.02, B2: 0.2, C2: 0.05 },
    mu_a_base: 0.1,
    mu_s_base: 50.0,
    anisotropyG: 0.1, // Highly diffuse scattering
  },
  {
    id: "aerogel",
    name: "Silica Aerogel [RICH Detector Threshold Material]",
    refractiveIndexStatic: 1.03, // Configurable threshold index
    sellmeierCoeffs: { B1: 0.05, C1: 0.001, B2: 0.01, C2: 0.005 },
    mu_a_base: 0.002,
    mu_s_base: 0.2,
    anisotropyG: 0.98,
  }
];
